import type { Bundle } from '../../types';

/**
 * WagePro — English string catalogue (source of truth).
 *
 * Keys are flat, dot-namespaced under a `wp.` prefix (`wp.<feature>.<key>`),
 * matching the app's existing `useTranslation('wp')` call sites.
 *
 * Extracted byte-for-byte out of the shared
 * `packages/jumpstone-i18n/src/ledger/strings/en/base.ts` monolith in Wave 3a
 * of the i18n consolidation epic (#22357, this wave #22358) — no
 * re-translation, no key renames, no value changes. See
 * `docs/epics/jumpstone-i18n-consolidation.md` for the wave schedule.
 */
export const en_base: Bundle = {
  'wp.common.actions': 'Actions',
  'wp.common.approve': 'Approve',
  'wp.common.back': 'Back',
  'wp.common.cancel': 'Cancel',
  'wp.common.close': 'Close',
  'wp.common.continue': 'Continue',
  'wp.common.edit': 'Edit',
  'wp.common.export': 'Export',
  'wp.common.none': 'None',
  'wp.common.save': 'Save',
  'wp.common.reject': 'Reject',
  'wp.common.review': 'Review',
  'wp.common.view': 'View',
  'wp.common.app_name': 'WagePro',
  // WagePro seeded pay components + statutory deductions.
  'wp.allowance.transport': 'Transport allowance',
  'wp.allowance.housing': 'Housing allowance',
  'wp.allowance.overtime': 'Overtime',
  'wp.allowance.meal': 'Meal allowance',
  'wp.allowance.performance_bonus': 'Performance bonus',
  'wp.deduction.epf_employee': 'EPF (employee)',
  'wp.deduction.etf_employer': 'ETF (employer)',
  'wp.deduction.apit': 'APIT',
  'wp.deduction.salary_advance': 'Salary advance repayment',
  'wp.deduction.staff_loan': 'Staff loan repayment',
  'wp.brand.logo_alt': 'WagePro logo',
  'wp.locale.switcher': 'Change language',
  'wp.locale.english': 'English',
  'wp.locale.sinhala': 'සිංහල',
  'wp.locale.tamil': 'தமிழ்',
  'wp.account.open': 'Open account menu',
  'wp.account.profile': 'My profile',
  'wp.account.sign_out': 'Sign out',
  'wp.account.switch_to_admin': 'Switch to admin view',
  'wp.account.switch_to_employee': 'Switch to employee view',
  'wp.account.preview_as_employee': 'Preview as employee (My pay) →',
  'wp.onboarding.switch_to_employee_tip.title':
    'Also an employee of this company?',
  'wp.onboarding.switch_to_employee_tip.body':
    'Add yourself as an employee using the email you sign in with. Then open the account menu (your avatar, top right) and choose “{switch_to_employee}” to see your own payslips and leave. To come back, choose “{switch_to_admin}”.',
  'wp.auth.step_up.title': 'Two-factor authentication required',
  'wp.auth.step_up.description':
    'Approving pay runs and recording or voiding statutory payments from WagePro requires a stronger sign-in than this session has.',
  'wp.auth.step_up.sign_in_again_cta': 'Sign in again',
  'wp.nav.primary': 'Primary navigation',
  'wp.nav.menu': 'Menu',
  'wp.nav.open_menu': 'Open navigation menu',
  'wp.nav.search_placeholder': 'Search employees…',
  'wp.nav.dashboard': 'Dashboard',
  'wp.nav.employees': 'Employees',
  'wp.nav.employees_directory': 'Directory',
  'wp.nav.employees_profile': 'Profile',
  'wp.nav.expenses': 'Expenses',
  'wp.nav.leave': 'Leave',
  'wp.nav.timesheets': 'Timesheets',
  'wp.nav.payroll': 'Payroll',
  'wp.nav.payroll_run': 'Run payroll',
  'wp.nav.payroll_history': 'Payroll history',
  'wp.nav.payroll_reports': 'Reports',
  'wp.nav.payroll_policies': 'Payroll policies',
  'wp.nav.data_centre': 'Data Centre',
  'wp.nav.dc_allowances': 'Allowances',
  'wp.nav.dc_deductions': 'Deductions',
  'wp.nav.dc_tax': 'Tax',
  'wp.nav.dc_bank': 'Bank',
  'wp.nav.dc_loans': 'Loans',
  'wp.nav.update_requests': 'Update requests',
  'wp.nav.settings': 'Settings',
  'wp.nav.settings_organisation': 'Organisation',
  'wp.nav.settings_policies': 'Policies',
  'wp.nav.settings_roles': 'Roles & permissions',
  'wp.nav.settings_operations': 'Operations',
  'wp.nav.settings_payroll': 'Payroll settings',
  'wp.notification.empty': 'Nothing new right now.',
  'wp.notification.heading': 'Notifications',
  'wp.notification.open': 'Notifications ({count} awaiting action)',
  'wp.notification.expenses_awaiting_approval':
    '{count, plural, one {# expense awaiting approval} other {# expenses awaiting approval}}',
  'wp.notification.filing_due':
    '{days, plural, one {APIT filing due in # day} other {APIT filing due in # days}}',
  'wp.notification.bank_update_requested': '{name} requested a bank update',
  'wp.dashboard.greeting_morning': 'Good morning, {name}',
  'wp.dashboard.greeting_afternoon': 'Good afternoon, {name}',
  'wp.dashboard.greeting_evening': 'Good evening, {name}',
  'wp.dashboard.subtitle': '{company} · Payroll period {period}',
  'wp.dashboard.stat.headcount': 'Headcount',
  'wp.dashboard.stat.headcount_delta': '{delta} this month',
  'wp.dashboard.stat.gross_payroll': 'Gross payroll this cycle',
  'wp.dashboard.stat.gross_payroll_delta': '{delta} vs last cycle',
  'wp.dashboard.stat.gross_payroll_no_previous_cycle': 'No previous cycle',
  'wp.dashboard.stat.pending_approvals': 'Pending approvals',
  'wp.dashboard.stat.pending_approvals_excludes_expenses':
    'Excludes expense claims (plan)',
  'wp.dashboard.stat.needs_review': 'Needs review',
  'wp.dashboard.stat.review_pending': 'Review now',
  'wp.dashboard.stat.compliance_alerts': 'Payroll filing alerts',
  'wp.dashboard.stat.compliance_caption_both':
    '{overdue, plural, one {# filing overdue} other {# filings overdue}} · {due_soon, plural, one {# filing due soon} other {# filings due soon}}',
  'wp.dashboard.stat.compliance_caption_overdue':
    '{overdue, plural, one {# filing overdue} other {# filings overdue}}',
  'wp.dashboard.stat.compliance_caption_due_soon':
    '{due_soon, plural, one {# filing due soon} other {# filings due soon}}',
  'wp.dashboard.stat.compliance_caption_none': 'Nothing overdue or due soon',
  'wp.dashboard.statutory.kicker': 'APIT · EPF/ETF filing',
  'wp.dashboard.statutory.title': '{period} statutory remittance',
  'wp.dashboard.statutory.due_in':
    '{days, plural, one {Due in # day} other {Due in # days}}',
  'wp.dashboard.statutory.due_today': 'Due today',
  'wp.dashboard.statutory.overdue_by':
    '{days, plural, one {Overdue by # day} other {Overdue by # days}}',
  'wp.dashboard.statutory.next': 'Next: {period} due {date}',
  'wp.dashboard.statutory.amount_due': 'Amount due',
  'wp.dashboard.statutory.filed_via': 'Filed via',
  'wp.dashboard.statutory.owner': 'Owner',
  'wp.dashboard.statutory.due_on': 'Due on',
  'wp.dashboard.action_needed': 'Action needed',
  'wp.dashboard.action_needed_empty': 'Nothing needs your attention',
  'wp.dashboard.action.expenses_pending':
    '{count, plural, one {# expense pending approval} other {# expenses pending approval}}',
  'wp.dashboard.action.update_requests_open':
    '{count, plural, one {# update request open} other {# update requests open}}',
  'wp.dashboard.action.permits_expiring':
    '{count, plural, one {# document expiring soon} other {# documents expiring soon}}',
  'wp.dashboard.action.joiners_missing_bank_details':
    '{count, plural, one {# new joiner missing bank details} other {# new joiners missing bank details}}',
  'wp.dashboard.recent_activity': 'Recent activity',
  'wp.dashboard.activity.exceptions_acknowledged':
    '{name} acknowledged {count, plural, one {# payroll exception} other {# payroll exceptions}}',
  'wp.dashboard.activity.pay_run_created': '{period} pay run created',
  'wp.dashboard.activity.pay_run_approved':
    '{name} approved the {period} pay run',
  'wp.dashboard.activity.expense_submitted':
    '{name} submitted an expense claim',
  'wp.dashboard.current_run': 'Current pay run — {period}',
  'wp.dashboard.continue_run': 'Continue run',
  'wp.dashboard.expiring_documents': 'Documents & renewals expiring',
  'wp.dashboard.document_owner': '{name} — {document}',
  'wp.dashboard.days_remaining': '{days, plural, one {# day} other {# days}}',
  'wp.dashboard.renewed': 'Renewed',
  'wp.employees.directory_title': 'Employee directory',
  'wp.employees.directory_subtitle':
    'Everyone employed by this company, with their current status.',
  'wp.employees.add': 'Add employee',
  'wp.employees.open_profile_for': "Open {name}'s profile",
  'wp.employees.sort_by_name': 'Sort by name',
  'wp.employees.column_employee': 'Employee',
  'wp.employees.column_department': 'Department',
  'wp.employees.column_role': 'Role',
  'wp.employees.column_type': 'Type',
  'wp.employees.column_status': 'Status',
  'wp.employees.no_employees_title': 'No employees yet',
  'wp.employees.no_employees_description':
    'Add your first employee to start running payroll.',
  'wp.employees.empty_title': 'No employees match your search',
  'wp.employees.empty_description':
    'Try a different name, department or job title.',
  'wp.employees.not_found': 'Employee not found',
  'wp.employees.not_found_description':
    'This record may have been removed. Return to the directory to pick another.',
  'wp.employees.profile_summary': '{role} · {department}',
  'wp.employees.tab_job': 'Job info',
  'wp.employees.tab_payroll': 'Payroll settings',
  'wp.employees.tab_bank': 'Bank info',
  'wp.employees.tab_tax': 'Tax info',
  'wp.employees.tab_documents': 'Documents',
  'wp.employees.tab_assets': 'Assets',
  'wp.employees.tab_performance': 'Performance',
  'wp.employees.tab_loans': 'Loans',
  'wp.employees.job_information': 'Job information',
  'wp.employees.job_title': 'Job title',
  'wp.employees.department': 'Department',
  'wp.employees.reports_to': 'Reports to',
  'wp.employees.employment_type': 'Employment type',
  'wp.employees.start_date': 'Start date',
  'wp.employees.work_location': 'Work location',
  'wp.employees.status_history': 'Employment status history',
  'wp.employees.event.joined': 'Joined as {role}',
  'wp.employees.event.promoted': 'Promoted to {role}',
  'wp.employees.event.on_leave': 'On leave — {reason}',
  'wp.employees.event.terminated': 'Employment ended — {reason}',
  'wp.employees.leave_reason.parental': 'parental leave',
  'wp.employees.leave_reason.medical': 'medical leave',
  'wp.employees.exit_reason.redundancy': 'role redundancy',
  'wp.employees.exit_reason.resignation': 'resignation',
  'wp.employees.pay_components': 'Pay components',
  'wp.employees.pay_frequency': 'Pay frequency',
  'wp.employees.basic_salary': 'Basic salary',
  'wp.employees.salary_structure': 'Salary structure',
  'wp.employees.salary_history': 'Effective-dated salary history',
  'wp.employees.standing_deductions': 'Standing deductions',
  'wp.employees.add_deduction': 'Add deduction',
  'wp.employees.no_deductions': 'No standing deductions',
  'wp.employees.no_deductions_description':
    'Standing deductions will appear here once added.',
  'wp.employees.deduction_type': 'Type',
  'wp.employees.deduction_amount': 'Amount',
  'wp.employees.deduction_effective_from': 'Effective from',
  'wp.employees.deduction_effective_to': 'Effective to',
  'wp.employees.deduction_effective_to_open': 'Open-ended',
  'wp.employees.deduction_reference': 'Reference',
  'wp.employees.deduction_reference_placeholder': 'e.g. court order number',
  'wp.employees.deduction_type.tax': 'Tax',
  'wp.employees.deduction_type.superannuation': 'Superannuation',
  'wp.employees.deduction_type.court_order': 'Court order',
  'wp.employees.deduction_type.other': 'Other',
  'wp.employees.add_deduction_title': 'Add standing deduction',
  'wp.employees.deduction_amount_required': 'Enter an amount.',
  'wp.employees.deduction_effective_from_required': 'Enter a start date.',
  'wp.employees.save_deduction': 'Save deduction',
  'wp.employees.remove_deduction': 'Remove',
  'wp.employees.remove_deduction_confirm_title': 'Remove deduction?',
  'wp.employees.remove_deduction_confirm_description':
    'This deduction will be removed from future pay runs immediately.',
  'wp.employees.remove_deduction_error':
    "Couldn't remove this deduction. Try again shortly.",
  'wp.employees.add_deduction_error':
    "Couldn't add this deduction. Try again shortly.",
  'wp.employees.deductions_load_error': "Couldn't load deductions. Try again.",
  'wp.employees.deductions_retry': 'Try again',
  'wp.employees.bank_information': 'Bank information',
  'wp.employees.bank': 'Bank',
  'wp.employees.account_number': 'Account number',
  'wp.employees.branch': 'Branch',
  'wp.employees.verification': 'Verification',
  'wp.employees.verified': 'Verified',
  'wp.employees.unverified': 'Not verified',
  'wp.employees.tax_information': 'Tax information',
  'wp.employees.tin': 'TIN',
  'wp.employees.apit_basis': 'APIT basis',
  'wp.employees.apit_basis_regular': 'Regular profits from employment',
  'wp.employees.exemptions': 'Exemptions declared',
  'wp.employees.column_document': 'Document',
  'wp.employees.column_uploaded': 'Uploaded',
  'wp.employees.column_asset': 'Asset',
  'wp.employees.column_issued': 'Issued',
  'wp.employees.column_condition': 'Condition',
  'wp.employees.column_cycle': 'Review cycle',
  'wp.employees.column_rating': 'Rating',
  'wp.employees.column_loan_type': 'Loan type',
  'wp.employees.column_principal': 'Principal',
  'wp.employees.column_outstanding': 'Outstanding',
  'wp.employees.column_monthly_deduction': 'Deduction / month',
  'wp.employees.no_documents': 'No documents on file',
  'wp.employees.no_assets': 'No assets issued',
  'wp.employees.no_reviews': 'No review cycles yet',
  'wp.employees.no_reviews_description':
    'Reviews appear here once a cycle closes.',
  'wp.employees.no_loans': 'No active loans',
  'wp.employees.no_loans_description':
    'Salary advances and staff loans appear here while they are being repaid.',
  'wp.employment_status.active': 'Active',
  'wp.employment_status.on_leave': 'On leave',
  'wp.employment_status.terminated': 'Terminated',
  'wp.employment_type.full_time': 'Full-time',
  'wp.employment_type.part_time': 'Part-time',
  'wp.employment_type.contract': 'Fixed-term contract',
  // WagePro worker types (MIGRATION_V384).
  'wp.employment_type.contractor': 'Contractor',
  'wp.employees.form_employment_type': 'Worker type',
  'wp.employees.form_employment_type_hint_full_time':
    'An employee working the standard full-time hours. Included in pay runs with full statutory contributions and leave.',
  'wp.employees.form_employment_type_hint_part_time':
    'An employee working fewer hours than full-time. Full statutory contributions; fixed and monthly leave entitlements are pro-rated by the percentage below.',
  'wp.employees.form_employment_type_hint_contract':
    'An employee on a fixed-term contract. Treated exactly like any other employee for pay runs, statutory contributions and leave.',
  'wp.employees.form_employment_type_hint_contractor':
    'A self-employed worker you engage for services. Recorded for reference only — not included in pay runs, statutory contributions, leave or self-service. Pay them through bills.',
  'wp.employees.form_contractor_to_employee_start_date_hint':
    'Switching from contractor to employee: update the employment start date to the day employment began. Leave accrues from that date, so an earlier contractor start date would count contractor months as employee service.',
  'wp.employees.form_fte_percent': '% of full-time',
  'wp.employees.form_fte_percent_hint':
    'Whole number from 1 to 100, e.g. 50 for half-time.',
  'wp.employees.form_error_fte_required':
    'Enter the percentage of full-time this person works.',
  'wp.employees.form_error_fte_out_of_range':
    'Enter a whole number from 1 to 100.',
  'wp.employees.form_contractor_pay_note':
    'Contractors are not paid through pay runs, so no salary or hourly rate is recorded here. Pay them through bills in LedgerPro.',
  'wp.employees.worker_types_banner_title': 'Worker types',
  'wp.employees.worker_types_banner_full_time':
    'Full-time — employees on standard hours, with full statutory contributions and leave.',
  'wp.employees.worker_types_banner_part_time':
    'Part-time — employees on reduced hours; statutory contributions apply and leave entitlements are pro-rated.',
  'wp.employees.worker_types_banner_contract':
    'Fixed-term contract — employees engaged for a set period, treated like any other employee.',
  'wp.employees.worker_types_banner_contractor':
    'Contractor — genuinely self-employed workers only. Recorded for reference; not included in pay runs, statutory contributions or leave, and paid through bills.',
  'wp.employees.filter_type_all': 'All',
  'wp.employees.filter_type_label': 'Filter by worker type',
  'wp.employees.fte_value': '{percent}% of full-time',
  'wp.employees.contractor_not_on_payroll_title': 'Not on payroll',
  'wp.employees.contractor_not_on_payroll_body':
    'This person is recorded as a contractor. Contractors are not included in pay runs, statutory contributions or leave. Pay them through bills in LedgerPro.',
  'wp.employees.contractor_no_ess':
    'Self-service access is only available to employees, not contractors.',
  'wp.employees.final_pay.contractor_note':
    'Contractors have no gratuity, severance or leave payout in WagePro — this will just end their engagement. Settle any final amount through a bill.',
  'wp.pay_run.contractors_excluded_note':
    '{count, plural, one {# contractor is not included in this pay run. Contractors are paid through bills.} other {# contractors are not included in this pay run. Contractors are paid through bills.}}',
  'wp.errors.contractor_not_eligible':
    'This action is only available for employees. Contractors are not included in payroll, leave or self-service.',
  'wp.errors.pay_run_has_contractor':
    'This pay run includes someone who is now recorded as a contractor. Delete the draft and create it again — contractors are left out automatically.',
  'wp.errors.contractor_on_draft_pay_run':
    'This worker is on a draft pay run. Delete or approve that pay run before recording them as a contractor.',
  'wp.errors.contractor_has_employee_history':
    'This worker has employee history (paid pay runs, a gratuity provision or leave). Recording them as a contractor would strand what they earned. Terminate and settle this employee, then add the contractor as a new worker.',
  'wp.errors.fte_not_part_time':
    'A percentage of full-time can only be set for a part-time worker, as a whole number from 1 to 100.',
  'wp.pay_run.blocked_contractor_title': 'This pay run cannot be approved',
  'wp.pay_run.blocked_contractor_body':
    '{count, plural, =0 {Someone on this draft is now recorded as a contractor.} one {# worker on this draft is now recorded as a contractor.} other {# workers on this draft are now recorded as contractors.}} Its totals cannot be shown. Delete the draft and create it again — contractors are left out automatically.',
  'wp.pay_run.blocked_contractor_short': 'Blocked — contractor on draft',
  'wp.pay_frequency.monthly': 'Monthly',
  'wp.pay_frequency.fortnightly': 'Fortnightly',
  'wp.pay_frequency.weekly': 'Weekly',
  'wp.document_status.active': 'Active',
  'wp.document_status.expiring_soon': 'Expiring soon',
  'wp.document_status.archived': 'Archived',
  'wp.asset_condition.good': 'Good',
  'wp.asset_condition.fair': 'Fair',
  'wp.asset_condition.poor': 'Poor',
  'wp.performance_rating.exceeds_expectations': 'Exceeds expectations',
  'wp.performance_rating.meets_expectations': 'Meets expectations',
  'wp.performance_rating.needs_improvement': 'Needs improvement',
  'wp.loan_kind.salary_advance': 'Salary advance',
  'wp.loan_kind.staff_loan': 'Staff loan',
  'wp.expenses.title': 'Expenses',
  'wp.expenses.subtitle':
    'Employee claims, from submission through to reimbursement.',
  'wp.expenses.column_employee': 'Employee',
  'wp.expenses.column_claim_number': 'Claim #',
  'wp.expenses.column_amount': 'Amount',
  'wp.expenses.column_submitted': 'Submitted',
  'wp.expenses.column_reimbursement': 'Reimbursement',
  'wp.expenses.reimbursed_separately': 'Reimbursed separately',
  'wp.expenses.reimbursed_via_payroll': 'Reimbursed via {period} payroll',
  'wp.expenses.reimbursed_via_payroll_pending':
    'Reimbursed via payroll (not yet scheduled)',
  'wp.expenses.total': 'Total {amount}',
  'wp.expenses.empty_title': 'Nothing in this queue',
  'wp.expenses.empty_description':
    'Claims move through pending, approved and paid as they are processed.',
  'wp.expenses.loading': 'Loading expense claims…',
  'wp.expenses.unknown_submitter': 'Unknown employee',
  'wp.expenses.no_entity_title': 'No company selected',
  'wp.expenses.no_entity_description':
    'Sign in again to choose the company whose expense claims you want to review.',
  'wp.expenses.entitlement_required_title': 'Expense claims not available',
  'wp.expenses.entitlement_required_description':
    'Expense claims require a Starter plan or higher. Upgrade your plan to review and approve claims here.',
  'wp.expenses.load_error_title': "Couldn't load expense claims",
  'wp.expenses.load_error_description':
    'Something went wrong loading the expense claim queue.',
  'wp.expenses.retry': 'Try again',
  'wp.expenses.action_error_entitlement_required':
    'Expense claims require a Starter plan or higher.',
  'wp.expenses.action_error_forbidden':
    "You don't have permission to approve or reject expense claims.",
  'wp.expenses.action_error_fx_rate_required':
    'This claim is in a foreign currency and cannot be approved yet — foreign-currency reimbursement is not supported.',
  'wp.expenses.action_error_generic': 'Something went wrong. Please try again.',
  'wp.expenses.reject_dialog_title': 'Reject claim {claim_number}',
  'wp.expenses.reject_dialog_description':
    'The employee will see this reason on their claim.',
  'wp.expenses.reject_reason_label': 'Reason for rejection',
  'wp.expenses.reject_reason_placeholder':
    'Explain why this claim is being rejected…',
  'wp.expenses.reject_reason_required':
    'A reason is required to reject a claim.',
  'wp.expense_status.pending': 'Pending',
  'wp.expense_status.approved': 'Approved',
  'wp.expense_status.paid': 'Paid',
  'wp.expense_status.rejected': 'Rejected',
  'wp.leave.title': 'Leave',
  'wp.leave.subtitle':
    'Employee leave requests, from submission through approval or decline.',
  'wp.leave.column_employee': 'Employee',
  'wp.leave.column_type': 'Type',
  'wp.leave.column_dates': 'Dates',
  'wp.leave.column_days': 'Days',
  'wp.leave.column_status': 'Status',
  'wp.leave.date_range': '{start} — {end}',
  'wp.leave.loading': 'Loading leave requests…',
  'wp.leave.unknown_employee': 'Unknown employee',
  'wp.leave.no_entity_title': 'No company selected',
  'wp.leave.no_entity_description':
    'Sign in again to choose the company whose leave requests you want to review.',
  'wp.leave.entitlement_required_title': 'Payroll add-on required',
  'wp.leave.entitlement_required_description':
    'Leave requests are part of the Payroll add-on. Add the Payroll add-on to review and approve requests here.',
  'wp.leave.load_error_title': "Couldn't load leave requests",
  'wp.leave.load_error_description':
    'Something went wrong loading the leave request queue.',
  'wp.leave.retry': 'Try again',
  'wp.leave.empty_title': 'Nothing in this queue',
  'wp.leave.empty_description':
    'Requests move through pending, approved and rejected as they are decided.',
  'wp.leave.action_error_entitlement_required':
    'Leave requests require the Payroll add-on.',
  'wp.leave.action_error_forbidden':
    "You don't have permission to approve or reject leave requests.",
  'wp.leave.action_error_not_found':
    'This leave request no longer exists. Refresh the list and try again.',
  'wp.leave.action_error_already_decided':
    'This leave request has already been approved or rejected. Refresh the list to see the current state.',
  'wp.leave.action_error_insufficient_balance':
    'This employee does not have enough leave balance to cover this request.',
  'wp.leave.action_error_stale_balance':
    'The leave balance changed while you were reviewing this request. Refresh and try again.',
  'wp.leave.action_error_generic': 'Something went wrong. Please try again.',
  'wp.leave.approve_dialog_title': 'Approve this leave request?',
  'wp.leave.approve_dialog_description':
    'Approving deducts the requested days from the employee’s balance where applicable.',
  'wp.leave.reject_dialog_title': 'Reject this leave request?',
  'wp.leave.reject_dialog_description':
    'The employee will see this reason on their request.',
  'wp.leave.reject_reason_label': 'Reason for rejection',
  'wp.leave.reject_reason_placeholder':
    'Explain why this request is being rejected…',
  'wp.leave.reject_reason_optional_help':
    'A reason is optional but recommended.',
  'wp.leave_status.pending': 'Pending',
  'wp.leave_status.approved': 'Approved',
  'wp.leave_status.rejected': 'Rejected',
  'wp.leave_type.annual': 'Annual leave',
  'wp.leave_type.sick': 'Sick leave',
  'wp.leave_type.unpaid': 'Unpaid leave',
  'wp.leave_type.other': 'Other',
  'wp.leave_type.casual': 'Casual leave',
  'wp.timesheets.title': 'Timesheets',
  'wp.timesheets.subtitle':
    'Hours worked by each employee, ready to feed into a pay run.',
  'wp.timesheets.log_time': 'Log time',
  'wp.timesheets.column_employee': 'Employee',
  'wp.timesheets.column_work_date': 'Date',
  'wp.timesheets.column_hours': 'Hours',
  'wp.timesheets.column_description': 'Notes',
  'wp.timesheets.loading': 'Loading time entries…',
  'wp.timesheets.unknown_employee': 'Unknown employee',
  'wp.timesheets.no_entity_title': 'No company selected',
  'wp.timesheets.no_entity_description':
    'Sign in again to choose the company whose time entries you want to review.',
  'wp.timesheets.entitlement_required_title': 'Payroll add-on required',
  'wp.timesheets.entitlement_required_description':
    'Timesheets are part of the Payroll add-on. Add the Payroll add-on to log and review time entries here.',
  'wp.timesheets.load_error_title': "Couldn't load time entries",
  'wp.timesheets.load_error_description':
    'Something went wrong loading the time-entry list.',
  'wp.timesheets.retry': 'Try again',
  'wp.timesheets.empty_title': 'No time entries yet',
  'wp.timesheets.empty_description':
    'Log an employee’s hours for a given day and they will appear here.',
  'wp.timesheets.form.title': 'Log time',
  'wp.timesheets.form.description_form':
    'Capture the hours an employee worked on a specific day.',
  'wp.timesheets.form.employee_label': 'Employee',
  'wp.timesheets.form.employee_placeholder': 'Select an employee',
  'wp.timesheets.form.employee_required': 'Choose an employee.',
  'wp.timesheets.form.work_date_label': 'Work date',
  'wp.timesheets.form.work_date_required': 'Pick the day this time was worked.',
  'wp.timesheets.form.hours_label': 'Hours',
  'wp.timesheets.form.hours_hint':
    'Enter decimal hours (e.g. 7.5). Max 168 per entry.',
  'wp.timesheets.form.hours_invalid':
    'Enter a positive number of hours, up to 168.',
  'wp.timesheets.form.description_label': 'Notes',
  'wp.timesheets.form.description_placeholder':
    'What was worked on (optional)…',
  'wp.timesheets.form.description_too_long':
    'Notes must be {max} characters or fewer.',
  'wp.timesheets.form.submit': 'Save entry',
  'wp.timesheets.form.error_entitlement_required':
    'Time entries require the Payroll add-on.',
  'wp.timesheets.form.error_forbidden':
    "You don't have permission to log time entries.",
  'wp.timesheets.form.error_not_found':
    'That employee no longer exists. Refresh and try again.',
  'wp.timesheets.form.error_generic': 'Something went wrong. Please try again.',
  'wp.update_requests.title': 'Update requests',
  'wp.update_requests.subtitle':
    'Changes employees have asked HR to make to their own records.',
  'wp.update_requests.column_type': 'Type',
  'wp.update_requests.column_requester': 'Requester',
  'wp.update_requests.column_submitted': 'Submitted',
  'wp.update_requests.column_status': 'Status',
  'wp.update_requests.view_diff': 'View change',
  'wp.update_requests.requested_by': 'Requested by {name} on {date}',
  'wp.update_requests.old_value': 'Current value',
  'wp.update_requests.new_value': 'Requested value',
  'wp.update_request_kind.bank_account': 'Bank account update',
  'wp.update_request_kind.address': 'Address correction',
  'wp.update_request_kind.dependent': 'Add dependent',
  'wp.update_request_kind.emergency_contact': 'Emergency contact',
  'wp.update_request_kind.name_correction': 'Name correction',
  'wp.update_request_status.in_review': 'In review',
  'wp.update_request_status.approved': 'Approved',
  'wp.update_request_status.rejected': 'Rejected',
  'wp.pay_run.title': 'Run payroll — {period}',
  'wp.pay_run.subtitle':
    '{count, plural, one {# employee in this run} other {# employees in this run}}',
  'wp.pay_run.run_payroll': 'Run payroll',
  'wp.pay_run.none_open_title': 'No pay run is open',
  'wp.pay_run.none_open_description':
    'Start a new cycle when the pay period opens.',
  'wp.pay_run.step_heading': 'Step {number} · {label}',
  'wp.pay_run.step.period_scope': 'Period & scope',
  'wp.pay_run.step.review_pay': 'Review pay',
  'wp.pay_run.step.statutory_summary': 'Statutory summary',
  'wp.pay_run.step.exceptions': 'Exceptions',
  'wp.pay_run.step.approval': 'Approval',
  'wp.pay_run.step.final_review': 'Final review',
  'wp.pay_run.step.post_run': 'Post-run',
  'wp.pay_run.pay_period': 'Pay period',
  'wp.pay_run.period_range': '{from} – {to} {period}',
  'wp.pay_run.employee_scope': 'Employee scope',
  'wp.pay_run.all_active_employees': 'All active employees ({count})',
  'wp.pay_run.pay_date': 'Pay date',
  'wp.pay_run.column_employee': 'Employee',
  'wp.pay_run.column_gross': 'Gross',
  'wp.pay_run.column_allowances': 'Incl. allowances',
  'wp.pay_run.column_deductions': 'Deductions',
  'wp.pay_run.column_net': 'Net',
  'wp.pay_run.shown_subtotal': 'Subtotal (rows shown)',
  'wp.pay_run.epf_employee': 'EPF — employee ({rate})',
  'wp.pay_run.epf_employer': 'EPF — employer ({rate})',
  'wp.pay_run.etf_employer': 'ETF — employer ({rate})',
  'wp.pay_run.apit_withheld': 'APIT withheld',
  'wp.pay_run.exceptions_blocking':
    '{count, plural, one {# item needs attention before you can continue} other {# items need attention before you can continue}}',
  'wp.pay_run.exceptions_blocking_help':
    'Resolve or acknowledge each exception to unlock the next step.',
  'wp.pay_run.exceptions_clear': 'No outstanding exceptions',
  'wp.pay_run.exceptions_clear_help': 'This run is clear to continue.',
  'wp.pay_run.exception.bank_details_incomplete':
    '{name} — bank information incomplete',
  'wp.pay_run.exception.new_joiner_unconfigured':
    'A new joiner is not yet configured in the Data Centre',
  'wp.pay_run.exception.time_logged_on_unpaid_leave':
    '{name} — time logged on an approved unpaid-leave day; those hours are not paid',
  'wp.pay_run.acknowledge': 'Acknowledge',
  'wp.pay_run.acknowledged': 'Acknowledged',
  'wp.pay_run.preparer': 'Preparer',
  'wp.pay_run.approver': 'Approver',
  'wp.pay_run.person_title': '{name}, {title}',
  'wp.pay_run.approval.awaiting': 'Awaiting sign-off',
  'wp.pay_run.approval.submitted': 'Submitted',
  'wp.pay_run.approval.approved': 'Approved',
  'wp.pay_run.sign_off': 'Sign off as approver',
  'wp.pay_run.confirm_locks_title': 'Confirming this run locks it',
  'wp.pay_run.confirm_locks_help':
    'Employee-level line items become read-only once confirmed. Amendments require a new run.',
  'wp.pay_run.generate_payslips': 'Generate payslips',
  'wp.pay_run.generate_bank_file': 'Generate bank payment file',
  'wp.pay_run.post_journal': 'Post GL journal entry',
  'wp.pay_run.mark_filings_due': 'Mark statutory filings as due',
  'wp.pay_run.summary': 'Pay run summary',
  'wp.pay_run.gross_wages': 'Gross wages',
  'wp.pay_run.total_allowances': 'Total allowances',
  'wp.pay_run.statutory_deductions': 'Statutory deductions',
  'wp.pay_run.other_deductions': 'Other deductions',
  'wp.pay_run.deduction_split_unreconciled':
    "The deduction breakdown could not be reconciled for this run. Net payable is payroll's own figure; the rows above are a best-effort split.",
  'wp.pay_run.net_payable': 'Net payable',
  'wp.pay_run.filing_due': 'Filing due {date}',
  'wp.pay_run_status.draft': 'Draft',
  'wp.pay_run_status.in_review': 'In review',
  'wp.pay_run_status.approved': 'Approved',
  'wp.pay_run_status.paid': 'Paid',
  'wp.pay_run_status.filed': 'Filed',
  'wp.pay_run_status.voided': 'Voided',
  'wp.history.title': 'Payroll history',
  'wp.history.subtitle':
    'Every completed cycle, with its immutable audit trail.',
  'wp.history.column_period': 'Period',
  'wp.history.column_gross': 'Gross',
  'wp.history.column_net': 'Net',
  'wp.history.column_status': 'Status',
  'wp.history.view_audit_trail': 'View audit trail',
  'wp.history.audit_trail': 'Audit trail',
  'wp.history.audit_trail_for': '{period} — audit trail',
  'wp.history.audit_meta': '{actor} · {date} at {time}',
  'wp.pay_run.audit.run_created': 'Run created',
  'wp.pay_run.audit.exceptions_acknowledged':
    '{count, plural, one {# exception acknowledged} other {# exceptions acknowledged}}',
  'wp.pay_run.audit.submitted_for_approval': 'Submitted for approval',
  'wp.pay_run.audit.approved': 'Approved',
  'wp.pay_run.audit.paid': 'Paid',
  'wp.pay_run.audit.bank_file_generated': 'Paid — bank file generated',
  'wp.pay_run.audit.statutory_filing_submitted': 'Statutory filing submitted',
  'wp.reports.title': 'Reports',
  'wp.reports.subtitle':
    'Export payroll data for filing, reconciliation and analysis.',
  'wp.reports.item.epf_form_c.name': 'EPF Form C',
  'wp.reports.item.epf_form_c.description':
    'Monthly EPF contribution return, ready for the Employees’ Provident Fund.',
  'wp.reports.item.etf_form_ii.name': 'ETF Form II',
  'wp.reports.item.etf_form_ii.description':
    'Monthly ETF contribution return, ready for the Employees’ Trust Fund Board.',
  'wp.reports.item.etf_form_r4.name': 'ETF Form R4',
  'wp.reports.item.etf_form_r4.description':
    'Annual ETF member statement covering every employee for the tax year.',
  'wp.reports.item.apit_monthly.name': 'APIT monthly return',
  'wp.reports.item.apit_monthly.description':
    'Monthly APIT (PAYE) remittance summary for IRD RAMIS filing.',
  'wp.reports.item.apit_annual.name': 'APIT annual reconciliation',
  'wp.reports.item.apit_annual.description':
    'Year-end APIT reconciliation of tax withheld against annual liability.',
  'wp.reports.period_month_label': 'Period (month)',
  'wp.reports.period_year_label': 'Period (year)',
  'wp.reports.exporting': 'Exporting…',
  'wp.reports.export_error': 'Couldn’t export this report. Try again shortly.',
  'wp.reports.export_entitlement_required':
    'This report needs the Payroll add-on to be active for this entity.',
  'wp.reports.export_not_available':
    'This report isn’t available for the current entity.',
  'wp.reports.export_missing_entity':
    'Pick an entity before exporting a report.',
  'wp.data_centre.title': 'Data Centre',
  'wp.data_centre.subtitle':
    'The reference data every pay run calculates from.',
  'wp.data_centre.add_allowance': 'Add allowance',
  'wp.data_centre.edit_allowance': 'Edit allowance',
  'wp.data_centre.allowance_name': 'Allowance name',
  'wp.data_centre.name_required': 'Enter a name for this allowance.',
  'wp.data_centre.name_too_long': 'Name must be {max} characters or fewer.',
  'wp.data_centre.save_allowance': 'Save allowance',
  'wp.data_centre.save_error':
    "Couldn't save this allowance. Try again shortly.",
  'wp.data_centre.add_deduction': 'Add deduction',
  'wp.data_centre.edit_deduction': 'Edit deduction',
  'wp.data_centre.deduction_name': 'Deduction name',
  'wp.data_centre.deduction_name_required': 'Enter a name for this deduction.',
  'wp.data_centre.save_deduction': 'Save deduction',
  'wp.data_centre.save_deduction_error':
    "Couldn't save this deduction. Try again shortly.",
  'wp.data_centre.loading': 'Loading pay items…',
  'wp.data_centre.no_entity_title': 'No company connected',
  'wp.data_centre.no_entity_description':
    "This account isn't linked to a company yet. Ask your administrator to grant access.",
  'wp.data_centre.entitlement_required_title':
    "Payroll access isn't enabled for this account yet",
  'wp.data_centre.entitlement_required_description':
    'Ask your administrator to enable the payroll add-on to see allowances and deductions.',
  'wp.data_centre.error_title': "We couldn't load the pay-item catalogue",
  'wp.data_centre.error_description':
    'This is usually temporary. If it keeps happening, contact your administrator.',
  'wp.data_centre.retry': 'Try again',
  'wp.data_centre.taxable': 'Taxable',
  'wp.data_centre.non_taxable': 'Non-taxable',
  'wp.data_centre.taxable_help':
    'Include this allowance in the APIT calculation basis.',
  'wp.data_centre.statutory': 'Statutory',
  'wp.data_centre.voluntary': 'Voluntary',
  'wp.data_centre.column_allowance': 'Allowance',
  'wp.data_centre.column_deduction': 'Deduction',
  'wp.data_centre.column_rule': 'Rule',
  'wp.data_centre.column_taxable': 'Taxable',
  'wp.data_centre.column_type': 'Type',
  'wp.data_centre.rule.fixed_monthly': 'Fixed · {amount} / month',
  'wp.data_centre.rule.percent_of_basic': '{rate} of basic salary',
  'wp.data_centre.rule.hourly_multiple': 'Formula · {multiple}× hourly rate',
  'wp.data_centre.rule.hourly_rate': 'Fixed · {amount} / hour',
  'wp.data_centre.rule.manager_input': 'Variable · entered by the manager',
  'wp.data_centre.rule.progressive_bracket': 'Progressive bracket',
  'wp.data_centre.rule.fixed_installment': 'Fixed · {amount} / instalment',
  'wp.data_centre.tax_moved_title':
    'APIT tax bands are shown on Payroll policies',
  'wp.data_centre.tax_moved_description':
    'The Inland Revenue bands applied to every pay run are shown read-only on the Payroll policies screen.',
  'wp.data_centre.tax_moved_action': 'Open Payroll policies',
  'wp.data_centre.file_format': 'Payment file format',
  'wp.data_centre.disbursement_account_label': 'Default disbursement account',
  'wp.data_centre.disbursement_account_placeholder': 'Select an account',
  'wp.data_centre.file_format_placeholder': 'e.g. CBC Bulk CSV v2',
  'wp.data_centre.file_format_too_long': 'Must be {max} characters or fewer.',
  'wp.data_centre.no_payment_source_accounts':
    'No bank or cash accounts are set up yet. Add one from the chart of accounts first.',
  'wp.data_centre.loan_annual_rate_label': 'Annual interest rate',
  'wp.data_centre.loan_max_installments_label': 'Maximum instalments',
  'wp.data_centre.rate_invalid': 'Enter a rate between 0% and 100%.',
  'wp.data_centre.installments_invalid':
    'Enter a whole number of instalments between 1 and 120.',
  'wp.data_centre.rate_type_label': 'Rate type',
  'wp.data_centre.rate_kind_fixed': 'Fixed amount',
  'wp.data_centre.rate_kind_per_hour': 'Per hour',
  'wp.data_centre.rate_kind_percentage': 'Percentage of basic salary',
  'wp.data_centre.rate_kind_manager_input':
    'Manager enters amount at pay-run time',
  'wp.data_centre.allowance_amount_label_fixed': 'Monthly amount',
  'wp.data_centre.allowance_amount_label_per_hour': 'Amount per hour',
  'wp.data_centre.allowance_rate_percent_label': 'Rate (%)',
  'wp.data_centre.allowance_amount_required':
    'Enter an amount greater than zero.',
  'wp.data_centre.allowance_rate_invalid':
    'Enter a percentage greater than 0%, up to 1000%.',
  'wp.data_centre.deduction_amount_label_fixed': 'Deduction amount',
  'wp.data_centre.deduction_amount_label_per_hour': 'Amount per hour',
  'wp.data_centre.deduction_rate_percent_label': 'Rate (%)',
  'wp.data_centre.deduction_amount_required':
    'Enter an amount greater than zero.',
  'wp.data_centre.deduction_rate_invalid':
    'Enter a percentage greater than 0%, up to 1000%.',
  'wp.data_centre.archive': 'Archive',
  'wp.data_centre.archive_confirm_title': 'Archive this pay item?',
  'wp.data_centre.archive_confirm_description':
    'This pay item will no longer appear in the active catalogue. Pay runs already generated are not affected.',
  'wp.data_centre.archive_error':
    'Could not archive this pay item. Please try again.',
  'wp.payroll_settings.loading': 'Loading payroll settings…',
  'wp.payroll_settings.no_entity_title': 'No company connected',
  'wp.payroll_settings.no_entity_description':
    "This account isn't linked to a company yet. Ask your administrator to grant access.",
  'wp.payroll_settings.entitlement_required_title':
    "Payroll access isn't enabled for this account yet",
  'wp.payroll_settings.entitlement_required_description':
    'Ask your administrator to enable the payroll add-on to manage these settings.',
  'wp.payroll_settings.error_title': "We couldn't load payroll settings",
  'wp.payroll_settings.error_description':
    'This is usually temporary. If it keeps happening, contact your administrator.',
  'wp.payroll_settings.retry': 'Try again',
  'wp.payroll_settings.save_error':
    "Couldn't save these settings. Try again shortly.",
  'wp.settings.title': 'Settings',
  'wp.settings.subtitle':
    'How this company runs payroll, and who is allowed to do what.',
  'wp.settings.tab_organisation': 'Organisation',
  'wp.settings.tab_payroll': 'Payroll',
  'wp.settings.legal_entity': 'Legal entity',
  'wp.settings.fiscal_year': 'Fiscal year',
  'wp.settings.fiscal_year_value': 'Starts {month}',
  'wp.settings.base_currency': 'Base currency',
  'wp.settings.locale': 'Locale',
  'wp.settings.pay_cycle_label': 'Pay cycle',
  'wp.settings.pay_cycle.monthly_last_working_day': 'Monthly, last working day',
  'wp.settings.pay_cycle.monthly_fixed_date': 'Monthly, fixed date',
  'wp.settings.holiday_calendar': 'Holiday calendar',
  'wp.settings.notifications': 'Notifications',
  'wp.settings.channel.email': 'Email',
  'wp.settings.channel.in_app': 'In-app',
  'wp.settings.channel.sms': 'SMS',
  'wp.settings.default_pay_frequency': 'Default pay frequency',
  'wp.settings.rounding_rule': 'Rounding rule',
  'wp.settings.proration_method': 'Proration method (joiners and leavers)',
  'wp.settings.overtime_basis': 'Overtime basis',
  'wp.settings.overtime_basis_value': 'Basic salary ÷ {hours} hours',
  'wp.settings.overtime_divisor_invalid':
    'Enter a whole number of hours between 1 and 10,000.',
  'wp.settings.save': 'Save changes',
  'wp.settings.saved': 'Saved',
  'wp.rounding_rule.nearest_unit': 'Nearest whole unit',
  'wp.rounding_rule.nearest_ten': 'Nearest ten',
  'wp.proration_method.calendar_day': 'Calendar-day basis',
  'wp.proration_method.working_day': 'Working-day basis',
  'wp.my_pay.title': 'My pay',
  'wp.my_pay.subtitle': '{name} · {employer}',
  'wp.my_pay.payslip_for': '{period} payslip',
  'wp.my_pay.paid_on': 'Paid {date}',
  'wp.my_pay.pending': 'Not yet paid',
  'wp.my_pay.line.basic_salary': 'Basic salary',
  'wp.my_pay.line.transport_allowance': 'Transport allowance',
  'wp.my_pay.line.gross_pay': 'Gross pay',
  'wp.my_pay.line.epf_employee': 'EPF',
  'wp.my_pay.line.apit_withheld': 'APIT withheld',
  'wp.my_pay.line.other_deductions': 'Other deductions',
  'wp.my_pay.line.other_earnings': 'Other earnings',
  'wp.my_pay.net_pay': 'Net pay',
  'wp.my_pay.download_error':
    "Couldn't download your payslip. Please try again.",
  'wp.my_pay.download_payslip': 'Download payslip',
  'wp.my_pay.download_saved': 'Payslip saved to your device.',
  'wp.my_pay.downloaded_notification_body': 'Tap to open',
  'wp.my_pay.downloaded_notification_title': 'Payslip downloaded',
  'wp.my_pay.history_empty': 'No earlier payslips.',
  'wp.my_pay.back_to_latest': 'Latest payslip',
  'wp.my_pay.history': 'Pay history',
  'wp.payroll_policies.title': 'Payroll policies',
  'wp.payroll_policies.subtitle':
    'Statutory contribution rates and payroll policies for Sri Lanka.',
  'wp.payroll_policies.statutory_badge': 'Statutory',
  'wp.payroll_policies.loading': 'Loading payroll policies…',
  'wp.payroll_policies.no_entity_title': 'No company connected',
  'wp.payroll_policies.no_entity_description':
    "This account isn't linked to a company yet. Ask your administrator to grant access.",
  'wp.payroll_policies.entitlement_required_title': 'Payroll add-on required',
  'wp.payroll_policies.entitlement_required_description':
    "This company doesn't have the payroll add-on enabled yet. Contact your account administrator to enable it.",
  'wp.payroll_policies.load_error_title': "Couldn't load payroll policies",
  'wp.payroll_policies.load_error_description':
    'This is usually temporary. If it keeps happening, contact your administrator.',
  'wp.payroll_policies.retry': 'Try again',
  'wp.payroll_policies.unsupported_title': 'Not available for this country',
  'wp.payroll_policies.unsupported_description':
    'Statutory payroll policies are not yet available for this entity’s country.',

  'wp.payroll_policies.epf.title': "EPF — Employees' Provident Fund",
  'wp.payroll_policies.epf.subtitle':
    "Contributions remitted monthly to the Employees' Provident Fund Board. Governed by EPF Act No. 15 of 1958.",
  'wp.payroll_policies.epf.employee_rate': 'Employee contribution',
  'wp.payroll_policies.epf.employee_rate_note': 'deducted from employee',
  'wp.payroll_policies.epf.employer_rate': 'Employer contribution',
  'wp.payroll_policies.epf.employer_rate_note': 'employer cost',
  'wp.payroll_policies.epf.combined_note':
    'Combined EPF contribution: {total} of gross wages per month.',

  'wp.payroll_policies.etf.title': "ETF — Employees' Trust Fund",
  'wp.payroll_policies.etf.subtitle':
    'Employer-only contribution. Governed by ETF Act No. 46 of 1980.',
  'wp.payroll_policies.etf.employer_rate': 'Employer contribution',
  'wp.payroll_policies.etf.employer_rate_note': 'employer cost only',

  'wp.payroll_policies.apit.title': 'APIT — Advance Personal Income Tax',
  'wp.payroll_policies.apit.subtitle':
    'Progressive tax withheld by employer and remitted to IRD. Brackets are set by the Inland Revenue Act.',
  'wp.payroll_policies.apit.col_income':
    'Monthly regular profits from employment',
  'wp.payroll_policies.apit.col_tax': 'Tax',
  'wp.payroll_policies.apit.row_up_to': 'Up to {amount}',
  'wp.payroll_policies.apit.row_between':
    'Exceeding {from} but not exceeding {to}',
  'wp.payroll_policies.apit.row_exceeding': 'Exceeding {from}',
  'wp.payroll_policies.apit.tax_relief': 'Relief from tax',
  'wp.payroll_policies.apit.tax_formula': '{rate} less {amount}',
  'wp.payroll_policies.apit.effective_from': 'Effective from {date}',
  'wp.payroll_policies.apit.in_force_on': 'Table in force on {date}',
  'wp.payroll_policies.apit.ird_note':
    'Brackets are fixed by the Inland Revenue Act and applied automatically to every pay run.',

  'wp.payroll_policies.gratuity.title': 'Gratuity',
  'wp.payroll_policies.gratuity.subtitle':
    'Half a month of basic salary per completed year of service, payable on resignation or retirement. Payment of Gratuity Act No. 12 of 1983.',
  'wp.payroll_policies.gratuity.min_years_label': 'Minimum qualifying service',
  'wp.payroll_policies.gratuity.years_suffix': 'completed years',
  'wp.payroll_policies.gratuity.rate_note':
    'Rate: ½ month of basic salary per completed year of service.',
  'wp.payroll_policies.gratuity.headcount_note':
    'Applies to employers with {count} or more employees.',
  'wp.payroll_policies.gratuity.payment_due_note':
    'Payable within {days} days of the end of employment.',
  'wp.dashboard.expiring_documents.empty_description':
    'Document expiry tracking is not available yet.',
  'wp.dashboard.expiring_documents.empty_title': 'No renewals tracked yet',
  'wp.dashboard.statutory.none_description':
    'Nothing due for EPF, ETF, or APIT in the current filing window.',
  'wp.dashboard.statutory.none_title': 'No statutory deadline due',
  'wp.my_pay.error': 'Something went wrong loading your pay. Please try again.',
  'wp.my_pay.issued_on': 'Approved {date}',
  'wp.my_pay.loading': 'Loading your payslip…',
  'wp.my_pay.no_employer_link_preview':
    'You are previewing the employee view. Payslips appear here once you are added as an employee of your company and have accepted the invite sent to your own email address.',
  'wp.my_pay.no_employer_link_employer_native':
    "Payslips appear here once you're added as an employee of your company. Setting up payroll and running pay are on the desktop app.",
  'wp.my_pay.link_self_cta': 'View my payslips',
  'wp.my_pay.link_self_error_no_match':
    'No employee record in this company uses your email address. Add yourself as an employee first, then try again.',
  'wp.my_pay.link_self_error_already_linked':
    'That employee record is already linked to another account.',
  'wp.my_pay.link_self_error_generic':
    'Could not open your payslips. Please try again.',
  'wp.my_pay.link_self_profile_title': 'Add your employment details',
  'wp.my_pay.link_self_profile_description':
    "You're not in the employee list yet. Enter your pay details so your payslips and leave balances are worked out correctly.",
  'wp.my_pay.link_self_profile_submit': 'Add me and view payslips',
  'wp.my_pay.link_self_error_rate_required':
    'An hourly employee requires an hourly rate greater than zero.',
  'wp.my_pay.no_employer_link':
    "You're not linked to an employer yet. Ask your employer to send you an invite by email, then accept it to see your payslips here.",
  'wp.my_pay.no_payslips': 'No payslips yet.',
  'wp.my_pay.retry': 'Try again',
  'wp.my_pay.select_employer': 'Employer',
  'wp.my_pay.step_up.description':
    'Viewing your payslips needs a stronger sign-in than this session has. Try again, and ask your administrator if it keeps happening.',
  'wp.my_pay.step_up.retry_cta': 'Try again',
  'wp.my_pay.step_up.title': 'Two-factor authentication required',
  'wp.pay_run.post_run.bank_boc': 'BOC',
  'wp.pay_run.post_run.bank_combank': 'Commercial Bank',
  'wp.pay_run.post_run.bank_hnb': 'HNB',
  'wp.pay_run.post_run.bank_sampath': 'Sampath Bank',
  'wp.pay_run.post_run.disbursement_account_label': 'Debit account number',
  'wp.pay_run.post_run.disbursement_account_placeholder': 'e.g. 001234567890',
  'wp.pay_run.post_run.disbursement_bank_label': 'Bank',
  'wp.pay_run.post_run.disbursement_bank_placeholder': 'Select a bank',
  'wp.pay_run.post_run.disbursement_dialog_title':
    'Generate salary disbursement file',
  'wp.pay_run.post_run.disbursement_error_generic':
    'Could not generate the disbursement file. Try again in a moment.',
  'wp.pay_run.post_run.disbursement_error_no_bank_details':
    'No employees in this pay run have complete bank details on file.',
  'wp.pay_run.post_run.disbursement_error_no_lines':
    'This pay run has no employee lines to disburse.',
  'wp.pay_run.post_run.disbursement_error_not_approved':
    'The disbursement file is available once this pay run is approved.',
  'wp.pay_run.post_run.disbursement_error_not_found':
    'Could not find this pay run. Refresh the page and try again.',
  'wp.pay_run.post_run.disbursement_generating': 'Generating…',
  'wp.pay_run.post_run.disbursement_open_dialog': 'Generate disbursement file',
  'wp.pay_run.post_run.disbursement_requires_approval':
    'Available once this pay run is approved.',
  'wp.pay_run.post_run.disbursement_submit': 'Generate & download',
  'wp.pay_run.post_run.disbursement_success':
    'Disbursement file downloaded — {count} employees, {amount}.',
  'wp.pay_run.post_run.disbursement_value_date_label': 'Value date',
  'wp.pay_run.post_run.disbursement_warnings_title':
    'Some employees were excluded from this file',
  'wp.pay_run.post_run.download_payslip': 'Download payslip',
  'wp.pay_run.post_run.filing_action_error':
    'Could not complete this action. Try again in a moment.',
  'wp.pay_run.post_run.filing_column_due': 'Due date',
  'wp.pay_run.post_run.filing_column_obligation': 'Obligation',
  'wp.pay_run.post_run.filing_column_status': 'Status',
  'wp.pay_run.post_run.filing_empty':
    'No filings yet — generate the upcoming schedule to see due dates.',
  'wp.pay_run.post_run.filing_generate_button': 'Generate upcoming filings',
  'wp.pay_run.post_run.filing_generating': 'Generating…',
  'wp.pay_run.post_run.filing_jurisdiction_unsupported':
    'The filing calendar is not available for this entity’s jurisdiction yet.',
  'wp.pay_run.post_run.filing_load_error':
    'Could not load the filing calendar.',
  'wp.pay_run.post_run.filing_mark_filed': 'Mark filed',
  'wp.pay_run.post_run.filing_mark_submitted': 'Mark submitted',
  'wp.pay_run.post_run.filing_permission_required':
    'You do not have permission to manage filings. Ask an administrator for access.',
  'wp.pay_run.post_run.filing_status_overdue': 'Overdue',
  'wp.pay_run.post_run.filing_status_submitted': 'Submitted',
  'wp.pay_run.post_run.filing_status_unknown': 'Unknown',
  'wp.pay_run.post_run.filing_status_upcoming': 'Upcoming',
  'wp.pay_run.post_run.filing_status_waived': 'Waived',
  'wp.pay_run.post_run.no_backend_description':
    'Payslips, the bank file, statutory remittances, and the filing calendar all call the real payroll API — connect one to use these actions.',
  'wp.pay_run.post_run.no_backend_title':
    'Post-run actions need a live backend connection',
  'wp.pay_run.post_run.payslip_downloading': 'Downloading…',
  'wp.pay_run.post_run.payslip_employee_label': 'Employee',
  'wp.pay_run.post_run.payslip_error':
    'Could not generate the payslip. Try again in a moment.',
  'wp.pay_run.post_run.payslip_no_employees':
    'No employees on this pay run yet.',
  'wp.pay_run.post_run.statutory_amount_due_label': 'Amount due',
  'wp.pay_run.post_run.statutory_bank_account_label': 'Paid from',
  'wp.pay_run.post_run.statutory_bank_account_load_error':
    'Could not load your bank accounts.',
  'wp.pay_run.post_run.statutory_bank_account_placeholder': 'Select an account',
  'wp.pay_run.post_run.statutory_bank_account_required':
    'Select the account this payment was made from.',
  'wp.pay_run.post_run.statutory_column_due': 'Amount due',
  'wp.pay_run.post_run.statutory_column_status': 'Status',
  'wp.pay_run.post_run.statutory_column_type': 'Type',
  'wp.pay_run.post_run.statutory_date_label': 'Payment date',
  'wp.pay_run.post_run.statutory_dialog_title': 'Record statutory payment',
  'wp.pay_run.post_run.statutory_error_already_voided':
    'This statutory payment has already been voided.',
  'wp.pay_run.post_run.statutory_error_amount_mismatch':
    'The amount does not match the computed amount due for this pay run.',
  'wp.pay_run.post_run.statutory_error_audience_required':
    'This sign-in is not authorised for payroll access. Sign in again and retry.',
  'wp.pay_run.post_run.statutory_error_bank_account_invalid':
    'The selected payment account is no longer available. Choose another account and try again.',
  'wp.pay_run.post_run.statutory_error_duplicate':
    'A payment of this type has already been recorded for this pay run.',
  'wp.pay_run.post_run.statutory_error_generic':
    'Could not complete this action. Try again in a moment.',
  'wp.pay_run.post_run.statutory_error_locked_period':
    'This date falls in a locked accounting period.',
  'wp.pay_run.post_run.statutory_error_not_approved':
    'Statutory payments can only be recorded once this pay run is approved.',
  'wp.pay_run.post_run.statutory_error_not_found':
    'Could not find this record. Refresh the page and try again.',
  'wp.pay_run.post_run.statutory_error_reconciled':
    'This payment is reconciled with the bank. Unreconcile it first, then void it.',
  'wp.pay_run.post_run.statutory_error_step_up_required':
    'This action requires two-factor authentication on your LedgerPro account. Set it up in your LedgerPro security settings, then sign in again here and retry.',
  'wp.pay_run.post_run.statutory_error_value_cap_exceeded':
    'This amount exceeds the value cap authorised for this connection.',
  'wp.pay_run.post_run.statutory_error_void_unsupported':
    'This payment was recorded before void support was added and cannot be voided automatically. Contact support for a manual correcting journal entry.',
  'wp.pay_run.post_run.statutory_load_error':
    'Could not load statutory payments.',
  'wp.pay_run.post_run.statutory_record_action': 'Record payment',
  'wp.pay_run.post_run.statutory_record_submit': 'Record payment',
  'wp.pay_run.post_run.statutory_recording': 'Recording…',
  'wp.pay_run.post_run.statutory_record_step_up_required':
    'Recording a statutory payment requires a stronger sign-in than this session has. Sign in again, and ask your administrator if it keeps happening.',
  'wp.pay_run.post_run.statutory_reference_label': 'Reference (optional)',
  'wp.pay_run.post_run.statutory_requires_approval':
    'Available once this pay run is approved.',
  'wp.pay_run.post_run.statutory_status_due': 'Due',
  'wp.pay_run.post_run.statutory_status_recorded': 'Recorded',
  'wp.pay_run.post_run.statutory_title': 'Statutory remittances',
  'wp.pay_run.post_run.statutory_type_epf_employee': 'EPF (employee)',
  'wp.pay_run.post_run.statutory_type_epf_employer': 'EPF (employer)',
  'wp.pay_run.post_run.statutory_type_etf': 'ETF',
  'wp.pay_run.post_run.statutory_type_paye': 'PAYE',
  'wp.pay_run.post_run.statutory_void_action': 'Void',
  'wp.pay_run.post_run.statutory_void_button': 'Void payment',
  'wp.pay_run.post_run.statutory_void_confirm_description':
    'This reverses the GL posting and lets you record a corrected amount.',
  'wp.pay_run.post_run.statutory_void_confirm_title': 'Void this payment?',
  'wp.pay_run.post_run.statutory_void_step_up_required':
    'Voiding a statutory payment requires a stronger sign-in than this session has. Sign in again, and ask your administrator if it keeps happening.',
  'wp.pay_run.start_pay_run': 'Start pay run',
  'wp.pay_run.start_new_pay_run': 'Start new pay run',
  'wp.pay_run.back_to_run': 'Back to {period} pay run',
  'wp.pay_run.start_pay_run_for': 'Start {period} pay run',
  'wp.pay_run.period_select_label': 'Payroll period',
  'wp.pay_run.period_select_placeholder': 'Select a month',
  'wp.pay_run.period_select_hint':
    'Pays employees for {start} to {end}, using their current salary and the pay items and deductions in effect by the period end.',
  'wp.pay_run.period_select_all_run':
    'Every recent month already has a pay run. Void a run to reprocess its month.',
  'wp.pay_run.period_option_already_run': '{period} (already run)',
  'wp.pay_run.starting': 'Starting…',
  'wp.pay_run.complete_title_approved': 'Pay run approved',
  'wp.pay_run.complete_title_paid': 'Pay run paid',
  'wp.pay_run.complete_title_filed': 'Pay run filed',
  'wp.pay_run.approved_by_on': 'Approved by {name} on {date}',
  'wp.pay_run.approved_on': 'Approved on {date}',
  'wp.pay_run.next_steps_title': 'Next steps',
  'wp.pay_run.view_details': 'View pay run details',
  'wp.pay_run.back_to_summary': 'Back to summary',
  'wp.settings.currency_locked_explanation':
    "Base currency can't be changed once transactions exist for this company.",
  'wp.settings.save_error': "Couldn't save changes. Please try again.",
  'wp.auth.grant_error.description':
    'We ran into a problem loading your payroll session. This is usually temporary — please try again.',
  'wp.auth.grant_error.retry': 'Try again',
  'wp.auth.grant_error.title': "We couldn't connect to your account",
  'wp.auth.network_error':
    "Can't reach the server. Check your internet connection and try again.",
  'wp.auth.unknown_error': 'Something went wrong. Please try again.',
  'wp.auth.no_payroll_access.description':
    "Your sign-in worked, but this account isn't linked to a company in WagePro yet. Ask your administrator to grant payroll access.",
  'wp.auth.no_payroll_access.title': "This account doesn't have payroll access",
  'wp.auth.session_error.description':
    'This is usually temporary. If it keeps happening, contact your administrator.',
  'wp.auth.session_error.retry': 'Try again',
  'wp.auth.session_error.title': "We couldn't load your session",
  'wp.auth.ledger_sso_handoff.title': 'Redirecting you to LedgerPro…',
  'wp.auth.ledger_sso_handoff.description':
    'Signing you in with your LedgerPro account. This only takes a moment.',
  'wp.auth.signed_out.description':
    'Your WagePro session on this device has ended. Sign in again to get back to your payroll.',
  'wp.auth.signed_out.sign_in': 'Sign in',
  'wp.auth.signed_out.title': "You're signed out",
  'wp.auth.signed_out.create_account': 'Create a company account',
  'wp.boot.starting': 'Starting WagePro…',
  'wp.boot.loading_account': 'Loading your WagePro account…',
  'wp.setup.title': 'Set up payroll for {org}',
  'wp.setup.subtitle':
    "Here's what LedgerPro already knows about your company. Confirm it looks right, then set up payroll to start running your first pay.",
  'wp.setup.from_ledger.heading': 'From LedgerPro',
  'wp.setup.field.legal_name': 'Legal name',
  'wp.setup.field.country': 'Country',
  'wp.setup.field.currency': 'Currency',
  'wp.setup.field.financial_year_end': 'Financial year-end',
  'wp.setup.field.registration_number': 'Registration number',
  'wp.setup.field.address': 'Address',
  'wp.setup.steps.heading': "What's next",
  'wp.setup.steps.coming_next': 'Coming next',
  'wp.setup.step.confirm_details.title': 'Confirm company details',
  'wp.setup.step.confirm_details.description':
    'Check the details LedgerPro already holds for this company.',
  'wp.setup.step.pay_schedule.title': 'Pay schedule',
  'wp.setup.step.pay_schedule.description':
    'Choose your pay frequency and first pay day.',
  'wp.setup.step.statutory.title': 'Statutory registrations',
  'wp.setup.step.statutory.description':
    'Register EPF/ETF or your local statutory contributions.',
  'wp.setup.step.first_employee.title': 'Add your first employee',
  'wp.setup.step.first_employee.description':
    'Bring in your team so you can run your first pay run.',
  'wp.setup.actions.set_up_payroll': 'Set up payroll',
  'wp.setup.actions.back_to_ledger': 'Back to LedgerPro',
  'wp.setup.loading.title': 'Loading company details…',
  'wp.setup.loading.description':
    'Just a moment while we fetch your details from LedgerPro.',
  'wp.setup.details_error.title': "Couldn't load company details",
  'wp.setup.details_error.description':
    'This is usually temporary. Try again in a moment.',
  'wp.setup.details_error.retry': 'Try again',
  'wp.setup.status_error.title': "Couldn't check your WagePro connection",
  'wp.setup.status_error.description':
    'This is usually temporary. Try again in a moment.',
  'wp.setup.status_error.retry': 'Try again',
  'wp.setup.error.network':
    "Can't reach the server. Check your internet connection and try again.",
  'wp.setup.error.unknown':
    'Something went wrong. Please try again in a moment.',
  'wp.setup.toast_connected': 'Connected to LedgerPro · {org}',
  'wp.setup.no_payroll_role.title':
    "Your LedgerPro role on {org} doesn't include payroll",
  'wp.setup.no_payroll_role.description':
    'Ask your firm administrator to add payroll access to your role, then come back here.',
  'wp.setup.employee_connected_note':
    "You're connected to {org} as an employee.",
  'wp.setup.payroll_inactive.title': "Payroll isn't active for {org}",
  'wp.setup.payroll_inactive.description':
    'Your LedgerPro organisation is connected, but the payroll add-on is not active. Ask your firm administrator, or go back to LedgerPro to activate payroll.',
  'wp.setup.error.forbidden':
    "You don't have permission to set up payroll for this organisation. Ask a firm owner or admin, or ask them to grant you the entity-admin role.",
  'wp.setup.error.subscription_required':
    "This organisation's LedgerPro subscription doesn't include payroll yet. Add it in LedgerPro and try again.",
  'wp.dashboard.activity.error_description':
    'Something went wrong loading the activity feed. Try again shortly.',
  'wp.dashboard.activity.error_title': "Couldn't load recent activity",
  'wp.dashboard.greeting_loading': 'Loading your dashboard…',
  'wp.dashboard.loading': 'Loading dashboard…',
  'wp.dashboard.statutory.error_description':
    'Something went wrong loading the statutory filing calendar. Try again shortly.',
  'wp.dashboard.statutory.error_title': "Couldn't load filing calendar",
  'wp.dashboard.tile_unavailable': 'Unavailable',
  'wp.employees.directory_entitlement_required_description':
    'Ask your administrator to enable the payroll add-on to see the employee directory.',
  'wp.employees.directory_entitlement_required_title':
    "Payroll access isn't enabled for this account yet",
  'wp.employees.directory_error_description':
    'This is usually temporary. If it keeps happening, contact your administrator.',
  'wp.employees.directory_error_title':
    "We couldn't load the employee directory",
  'wp.employees.directory_loading': 'Loading employees…',
  'wp.employees.directory_no_entity_description':
    "This account isn't linked to a company yet. Ask your administrator to grant access.",
  'wp.employees.directory_no_entity_title': 'No company connected',
  'wp.employees.directory_retry': 'Try again',
  'wp.employees.profile_entitlement_required_description':
    'Ask your administrator to enable the payroll add-on to view employee profiles.',
  'wp.employees.profile_entitlement_required_title':
    "Payroll access isn't enabled for this account yet",
  'wp.employees.profile_error_description':
    'This is usually temporary. If it keeps happening, contact your administrator.',
  'wp.employees.profile_error_title': "We couldn't load this employee",
  'wp.employees.profile_loading': 'Loading employee…',
  'wp.employees.profile_retry': 'Try again',
  // Employee create/edit form (#22335).
  'wp.employees.form_create_title': 'Add employee',
  'wp.employees.form_create_description':
    "Enter the new employee's details below.",
  'wp.employees.form_edit_title': 'Edit employee',
  'wp.employees.form_edit_description': "Update this employee's details below.",
  'wp.employees.form_full_name': 'Full Name (as per ID)',
  'wp.employees.form_full_name_hint':
    'Enter the name exactly as shown on the ID card.',
  'wp.employees.form_email': 'Email',
  'wp.employees.form_pay_basis': 'Pay basis',
  'wp.employees.form_pay_basis_hourly': 'Hourly',
  'wp.employees.form_pay_basis_salaried': 'Salaried',
  'wp.employees.form_monthly_salary': 'Monthly salary ({currency})',
  'wp.employees.form_hourly_rate': 'Hourly rate',
  'wp.employees.form_hourly_rate_currency': 'Hourly rate ({currency})',
  'wp.employees.form_pay_currency': 'Pay currency',
  'wp.employees.form_pay_currency_foreign_hint':
    'Payroll runs in {base} only for now, so this employee is left out of pay runs until their pay currency is {base}.',
  'wp.employees.form_start_date': 'Start date',
  'wp.employees.form_termination_date': 'Termination date',
  // Labels for the shared @jumpstone/ui-react DatePicker (Start/Termination
  // date fields above) -- the component ships no English fallbacks, so
  // every caller must supply these via its `labels` prop.
  'wp.date_picker.placeholder': 'DD Mon YYYY',
  'wp.date_picker.previous_month': 'Previous month',
  'wp.date_picker.next_month': 'Next month',
  'wp.date_picker.open_calendar': 'Open calendar',
  'wp.date_picker.clear': 'Clear',
  'wp.date_picker.today': 'Today',
  'wp.employees.form_is_active': 'Active',
  'wp.employees.form_job_title': 'Job title',
  'wp.employees.form_department': 'Department',
  'wp.employees.form_work_location': 'Work location',
  'wp.employees.form_work_location_hint':
    'Enter both the city and country, e.g. Colombo, Sri Lanka.',
  'wp.employees.form_bank_name': 'Bank name',
  'wp.employees.form_bank_account_number': 'Account number',
  'wp.employees.form_bank_branch': 'Branch',
  'wp.employees.form_bank_code': 'Branch code',
  'wp.employees.form_nic_number': 'NIC number',
  'wp.employees.form_epf_member_no': 'EPF member number',
  'wp.employees.form_employee_number': 'Employee number',
  'wp.employees.form_employee_number_hint':
    'Optional. Used to match rows when importing prior payroll.',
  'wp.employees.error_employee_number_taken':
    'That employee number is already in use.',
  'wp.employees.form_npwp': 'NPWP',
  'wp.employees.form_nik': 'NIK',
  'wp.employees.form_ptkp_status': 'PTKP status',
  'wp.employees.form_ptkp_unset': 'Not set',
  'wp.employees.form_bpjs_jkk_risk_class': 'BPJS JKK risk class',
  'wp.employees.form_bpjs_jkk_risk_class_option': 'Risk class {risk_class}',
  'wp.employees.form_residency_status': 'Tax residency',
  'wp.employees.form_residency_resident': 'Resident',
  'wp.employees.form_residency_non_resident': 'Non-resident',
  'wp.employees.form_is_citizen': 'Sri Lankan citizen',
  'wp.employees.form_is_citizen_yes': 'Yes',
  'wp.employees.form_is_citizen_no': 'No',
  'wp.employees.form_non_resident_non_citizen_hint':
    'No personal relief (IRD Table 04); the tax withheld is final.',
  'wp.employees.form_apit_employment_type': 'Employment with this employer',
  'wp.employees.form_apit_employment_primary': 'Primary employment',
  'wp.employees.form_apit_employment_secondary': 'Secondary employment',
  'wp.employees.form_apit_employment_hint_primary':
    'The employee’s only or primary job: the monthly relief applies.',
  'wp.employees.form_apit_employment_hint_secondary':
    'Another employer holds the primary job, or no primary declaration was furnished here: a flat rate applies to all pay (Table 07).',
  'wp.employees.form_apit_declared_primary_income':
    'Declared monthly income from primary employment',
  'wp.employees.form_apit_declared_primary_income_hint':
    'From the secondary employment declaration. Leave blank if none was furnished; APIT is then withheld at 36% of all pay.',
  'wp.employees.form_apit_borne_by_employer':
    "Employer pays this employee's income tax",
  'wp.employees.form_apit_borne_by_employer_hint':
    'Tax-on-tax (IRD Table 06): pay is treated as the net amount the employee receives and the employer pays the income tax on top.',
  'wp.employees.form_apit_borne_unavailable_hint':
    'Not available for non-resident non-citizens (Table 04) or resident secondary employment (Table 07): the IRD has no tax-on-tax table for them.',
  'wp.employees.apit_basis_primary': 'Primary employment',
  'wp.employees.apit_basis_primary_non_resident':
    'Primary employment (Table 04)',
  'wp.employees.apit_basis_secondary': 'Secondary employment (Table 07)',
  'wp.employees.apit_basis_employer_borne':
    'Table 06 — income tax borne by employer',
  'wp.employees.apit_basis_employer_borne_secondary':
    'Table 6.3 — non-resident secondary, tax borne by employer',
  'wp.employees.apit_declared_primary_income': 'Declared primary income',
  'wp.employees.apit_not_declared': 'Not declared (36%)',
  'wp.employees.residency_non_citizen_suffix': '(non-citizen, Table 04)',
  'wp.employees.form_plan_cap_title': 'Employee limit reached',
  'wp.employees.form_plan_cap_fallback':
    'Your plan has reached its payroll employee limit. Remove an employee, or upgrade to add more.',
  'wp.employees.form_entitlement_required_title':
    "Payroll access isn't enabled for this account yet",
  'wp.employees.form_entitlement_required_description':
    'Ask your administrator to enable the payroll add-on to add or edit employees.',
  'wp.employees.form_validation_error_title': "Couldn't save this employee",
  'wp.employees.form_save_error_title': "Couldn't save this employee",
  'wp.employees.form_save_error_description':
    'Something went wrong. Please try again.',
  'wp.employees.form_error_required': 'This field is required.',
  'wp.employees.form_error_too_long': 'This value is too long.',
  'wp.employees.form_error_invalid_email': 'Enter a valid email address.',
  'wp.employees.form_error_salary_required':
    'A salaried employee requires a monthly salary greater than zero.',
  'wp.employees.form_error_negative_amount': 'Amount must not be negative.',
  'wp.employees.form_error_termination_before_start':
    'Termination date must be on or after the start date.',
  'wp.employees.form_error_invalid_nic':
    'Enter a 9-digit NIC ending in V/X, or a 12-digit NIC.',
  'wp.employees.form_error_invalid_nic_day':
    'This NIC number has an invalid day-of-year.',
  'wp.employees.form_error_epf_empty': 'EPF member number cannot be blank.',
  'wp.employees.form_error_epf_control_chars':
    'EPF member number contains invalid characters.',
  'wp.employees.form_error_invalid_risk_class':
    'Select a BPJS JKK risk class between 1 and 5.',
  'wp.history.audit_empty': 'No audit events recorded for this pay run yet.',
  'wp.history.audit_entitlement_required':
    'The audit trail is not available on your current plan.',
  'wp.history.audit_error': 'Could not load the audit trail. Please try again.',
  'wp.history.audit_incomplete':
    'Could not confirm whether this pay run has audit events — too much recent activity to search through. Contact support if you need to check the full history.',
  'wp.history.audit_loading': 'Loading audit trail…',
  'wp.history.audit_no_entity':
    'Your session no longer has access to this payroll entity.',
  'wp.history.audit_retry': 'Try again',
  'wp.pay_run.entitlement_required_description':
    "This company doesn't have the payroll add-on enabled yet. Contact your account administrator to enable it.",
  'wp.pay_run.entitlement_required_title': 'Payroll add-on required',
  'wp.pay_run.exception_ack_unavailable':
    "Acknowledging exceptions isn't available for this pay run yet.",
  'wp.pay_run.load_error_description':
    'Something went wrong. Please try again.',
  'wp.pay_run.load_error_title': "Couldn't load pay run history",
  'wp.pay_run.loading': 'Loading pay run history…',
  'wp.pay_run.post_run.disbursement_account_load_error':
    'Could not load your payment accounts. Try again.',
  'wp.pay_run.post_run.disbursement_account_picker_label':
    'Quick-fill from an account',
  'wp.pay_run.post_run.disbursement_account_select_placeholder':
    'Select an account',
  'wp.pay_run.post_run.disbursement_entitlement_required':
    "This entity doesn't have the payroll add-on enabled. Contact support to enable it.",
  'wp.pay_run.post_run.disbursement_success_count_only':
    'Disbursement file downloaded — {count} employees.',
  'wp.pay_run.post_run.payslip_entitlement_required':
    "This entity doesn't have the payroll add-on enabled. Contact support to enable it.",
  'wp.pay_run.post_run.read_only_notice':
    'Your access is read-only. Recording payments, generating filings, and marking them as filed require write access.',
  'wp.pay_run.post_run.statutory_entitlement_required':
    "This entity doesn't have the payroll add-on enabled. Contact support to enable it.",
  'wp.pay_run.read_only_notice':
    "Your access is read-only — starting or signing off a pay run isn't available.",
  'wp.pay_run.retry': 'Try again',
  'wp.pay_run.sign_off_error':
    "Couldn't sign off this pay run. Please try again.",
  'wp.pay_run.sign_off_step_up_required':
    'Signing off pay runs requires a stronger sign-in than this session has. Sign in again, and ask your administrator if it keeps happening.',
  'wp.pay_run.signing_off': 'Signing off…',
  'wp.pay_run.skipped_foreign_currency':
    "{count, plural, one {# employee was} other {# employees were}} left out of this pay run because they aren't paid in your base currency: {names}",
  'wp.pay_run.start_error': "Couldn't start a new pay run. Please try again.",
  'wp.pay_run.start_step_up_required':
    'Starting a new pay run requires a stronger sign-in than this session has. Sign in again, and ask your administrator if it keeps happening.',
  'wp.pay_run.finalise_cta': 'Finalise pay run',
  'wp.pay_run.finalising': 'Finalising…',
  'wp.pay_run.finalise_error':
    "Couldn't finalise this pay run. Please try again.",
  'wp.pay_run.finalise_step_up_required':
    'Finalising a pay run requires a stronger sign-in than this session has. Sign in again, and ask your administrator if it keeps happening.',
  'wp.pay_run.finalise_status_locked':
    'This pay run is {status} — finalising is only available on approved runs.',
  'wp.history.void_cta': 'Void',
  'wp.history.delete_cta': 'Delete',
  'wp.history.void_confirm_title': 'Void this pay run?',
  'wp.history.void_confirm_description':
    'The {period} pay run will be voided and its posted journal entries reversed.',
  'wp.history.delete_confirm_title': 'Delete this draft pay run?',
  'wp.history.delete_confirm_description':
    'The {period} draft pay run will be deleted. This cannot be undone.',
  'wp.history.void_error': "Couldn't void this pay run. Please try again.",
  'wp.history.delete_error': "Couldn't delete this pay run. Please try again.",
  // #23309 — per-line adjustment/lump-sum editor (wizard's Review Pay step).
  'wp.pay_run.line.edit_cta': 'Edit',
  'wp.pay_run.line.edit_column': 'Edit line',
  'wp.pay_run.line.edit_row_aria': "Edit {name}'s pay",
  'wp.pay_run.line.edit_title': 'Adjust pay for {name}',
  'wp.pay_run.line.adjustment_label': 'Adjustment amount',
  'wp.pay_run.line.adjustment_help':
    'Positive or negative amount to add to gross pay. Leave blank to clear.',
  'wp.pay_run.line.adjustment_reason_label': 'Reason (optional)',
  'wp.pay_run.line.reason_too_long':
    'Reason is too long. Keep it under {max} characters.',
  'wp.pay_run.line.lump_sum_label': 'Lump sum amount',
  'wp.pay_run.line.lump_sum_help':
    'Non-negative lump-sum amount (e.g. bonus). Leave blank to clear.',
  'wp.pay_run.line.save_cta': 'Save changes',
  'wp.pay_run.line.saving': 'Saving…',
  'wp.pay_run.line.save_error': "Couldn't save the changes. Please try again.",
  'wp.pay_run.line.save_step_up_required':
    'Editing pay-run lines requires a stronger sign-in than this session has. Sign in again, and ask your administrator if it keeps happening.',
  // #23313 — WagePro employee termination + final-pay flow.
  'wp.employees.terminate.action_button': 'Terminate',
  'wp.employees.terminate.confirm_title': 'Terminate this employee?',
  'wp.employees.terminate.confirm_description':
    "{name} will be marked as terminated and their access to their own payslip data will be revoked. This can't be undone from here.",
  'wp.employees.terminate.date_label': 'Termination date',
  'wp.employees.terminate.reason_label': 'Reason (optional)',
  'wp.employees.terminate.reason_placeholder':
    'e.g. resignation, redundancy, end of contract',
  'wp.employees.terminate.confirm_cta': 'Terminate employee',
  'wp.employees.terminate.continue_to_final_pay_cta': 'Continue to final pay',
  'wp.employees.terminate.cancel_cta': 'Cancel',
  'wp.employees.terminate.success': '{name} has been terminated.',
  'wp.employees.terminate.already_terminated_error':
    'This employee has already been terminated. Refresh the profile to see the current status.',
  'wp.employees.terminate.entitlement_required_error':
    "This entity doesn't have the payroll add-on enabled. Contact support to enable it.",
  'wp.employees.terminate.error_generic':
    "Couldn't terminate this employee. Please try again.",
  'wp.employees.final_pay.drawer_title': 'Final pay — {name}',
  'wp.employees.final_pay.lk_gratuity_heading':
    'Sri Lankan gratuity settlement (Payment of Gratuity Act No. 12 of 1983).',
  'wp.employees.final_pay.lk_provision_error':
    'Could not load the gratuity provision. Try again.',
  'wp.employees.final_pay.lk_headcount_label': 'Active headcount',
  'wp.employees.final_pay.lk_existing_provision_label':
    'Carried provision (827)',
  'wp.employees.final_pay.lk_not_applicable':
    "The Payment of Gratuity Act doesn't apply — the entity is below the statutory headcount threshold.",
  'wp.employees.final_pay.lk_ytd_remuneration_label':
    'Year-to-date taxable remuneration',
  'wp.employees.final_pay.lk_ytd_remuneration_help':
    'Used to compute IRD Tax Table 3 retention on the terminal gratuity payment.',
  'wp.employees.final_pay.lk_scheme_approved_label':
    'Employer scheme is IRD-approved',
  'wp.employees.final_pay.lk_bank_account_label': 'Payment source account',
  'wp.employees.final_pay.lk_bank_account_placeholder':
    'Select a bank/cash account',
  'wp.employees.final_pay.lk_settle_cta': 'Settle and terminate',
  'wp.employees.final_pay.lk_settle_success':
    "{name}'s gratuity settlement has been posted and the employee terminated.",
  'wp.employees.final_pay.id_pesangon_heading':
    'Indonesian pesangon (severance) — PP 35/2021.',
  'wp.employees.final_pay.id_termination_reason_label': 'Termination reason',
  'wp.employees.final_pay.id_unused_leave_label': 'Unused annual leave',
  'wp.employees.final_pay.id_unused_leave_help':
    'Cash value of unused annual leave (folded into UPH).',
  'wp.employees.final_pay.id_repatriation_label': 'Repatriation cost',
  'wp.employees.final_pay.id_repatriation_help':
    'Repatriation cost cash-out, if any.',
  'wp.employees.final_pay.id_other_uph_label': 'Other UPH compensation',
  'wp.employees.final_pay.id_other_uph_help':
    'Other UPH rights-compensation, if any.',
  'wp.employees.final_pay.id_separation_pay_label': 'Uang pisah',
  'wp.employees.final_pay.id_separation_pay_help':
    'Uang pisah — only for reasons PP 35/2021 permits it.',
  'wp.employees.final_pay.id_reason.merger_acquisition':
    'Merger or acquisition',
  'wp.employees.final_pay.id_reason.takeover': 'Takeover of the business',
  'wp.employees.final_pay.id_reason.takeover_worker_refuses_changed_terms':
    'Takeover — worker refuses changed terms',
  'wp.employees.final_pay.id_reason.efficiency_due_to_losses':
    'Efficiency measures due to losses',
  'wp.employees.final_pay.id_reason.efficiency_to_prevent_losses':
    'Efficiency measures to prevent losses',
  'wp.employees.final_pay.id_reason.closure_due_to_losses':
    'Closure due to sustained losses',
  'wp.employees.final_pay.id_reason.closure_not_due_to_losses':
    'Closure not due to losses',
  'wp.employees.final_pay.id_reason.closure_force_majeure':
    'Closure due to force majeure',
  'wp.employees.final_pay.id_reason.force_majeure_no_closure':
    'Force majeure without closure',
  'wp.employees.final_pay.id_reason.pkpu_due_to_losses': 'PKPU due to losses',
  'wp.employees.final_pay.id_reason.pkpu_not_due_to_losses':
    'PKPU not due to losses',
  'wp.employees.final_pay.id_reason.bankruptcy': 'Bankruptcy',
  'wp.employees.final_pay.id_reason.worker_requested_employer_wrongdoing':
    'Worker requests termination — employer wrongdoing',
  'wp.employees.final_pay.id_reason.court_found_no_employer_wrongdoing':
    'Court found no employer wrongdoing',
  'wp.employees.final_pay.id_reason.resignation': 'Resignation',
  'wp.employees.final_pay.id_reason.absence_5_days':
    'Absent 5 or more consecutive days',
  'wp.employees.final_pay.id_reason.violation_after_warnings':
    'Violation after warnings',
  'wp.employees.final_pay.id_reason.urgent_violation': 'Urgent violation',
  'wp.employees.final_pay.id_reason.detained_company_loss':
    'Detained — company suffering losses',
  'wp.employees.final_pay.id_reason.detained_not_company_loss':
    'Detained — no company loss',
  'wp.employees.final_pay.id_reason.prolonged_illness_or_disability':
    'Prolonged illness or disability',
  'wp.employees.final_pay.id_reason.retirement': 'Retirement',
  'wp.employees.final_pay.id_reason.death': 'Death of employee',
  'wp.employees.final_pay.id_expense_account_label': 'Pesangon expense account',
  'wp.employees.final_pay.id_payable_account_label': 'Pesangon payable account',
  'wp.employees.final_pay.id_submit_cta': 'Post pesangon and terminate',
  'wp.employees.final_pay.id_pesangon_success':
    '{name} has been terminated and pesangon posted.',
  'wp.employees.final_pay.other_jurisdiction_note':
    "There's no jurisdiction-specific final-pay settlement to post here — this will just mark the employee as terminated.",

  // ── ESS invite management (#23314) ────────────────────────────────────────
  'wp.employees.tab_access': 'Self-service access',
  'wp.employees.ess_tab_title': 'Employee self-service',
  'wp.employees.ess_tab_description':
    'Invite this employee to view their own payslips online. The link is single-use, expires in 7 days, and only lets them see their own payroll data.',
  'wp.employees.column_self_service': 'Self-service',
  'wp.employees.ess_status_no_email': 'No email on file',
  'wp.employees.ess_status_expired': 'Invitation expired',
  'wp.employees.ess_invite_status_expired': 'Expired',
  'wp.employees.ess_action_resend': 'Resend invite',
  'wp.employees.ess_action_reinvite': 'Re-invite',
  'wp.employees.ess_expires_on': 'Expires {date}',
  'wp.employees.ess_expired_on': 'Expired {date}',
  'wp.employees.ess_added_and_invited_title': 'Employee added and invited',
  'wp.employees.ess_added_not_invited_title':
    "Employee added, but the invitation wasn't sent",
  'wp.employees.ess_invite_failed_title': 'Invitation not sent',
  'wp.employees.ess_directory_invite_emailed':
    'We emailed a WagePro self-service invitation to {email}. It expires in 72 hours.',
  'wp.employees.form_invite_ess_label': 'Invite to WagePro self-service',
  'wp.employees.form_invite_ess_hint':
    "We'll email them a link to set a password and view their own payslips.",
  'wp.employees.form_invite_ess_needs_email':
    'Add an email address above to invite this employee.',
  'wp.employees.ess_since': 'Since {date}',
  'wp.employees.column_self_service_actions': 'Self-service actions',
  'wp.employees.ess_directory_email_failed':
    "We couldn't email the invitation to {email}. Copy the invite link and share it with them yourself, or resend the invite.",
  'wp.employees.ess_status': 'Status',
  'wp.employees.ess_status_not_invited': 'Not invited',
  'wp.employees.ess_status_pending': 'Invitation sent',
  'wp.employees.ess_status_linked': 'Accepted',
  'wp.employees.ess_status_revoked': 'Invite revoked',
  'wp.employees.ess_send_invite': 'Send invite',
  'wp.employees.ess_revoke_invite': 'Revoke invite',
  'wp.employees.ess_revoke_access': 'Revoke access',
  'wp.employees.ess_history_title': 'Invite history',
  'wp.employees.ess_history_column_status': 'Status',
  'wp.employees.ess_history_column_created': 'Sent',
  'wp.employees.ess_history_column_expires': 'Expires',
  'wp.employees.ess_history_column_actions': 'Actions',
  'wp.employees.ess_history_empty': 'No invites sent yet.',
  'wp.employees.ess_invite_status_pending': 'Pending',
  'wp.employees.ess_invite_status_accepted': 'Accepted',
  'wp.employees.ess_invite_status_revoked': 'Revoked',
  'wp.employees.ess_invite_sent_title': 'Invite ready to share',
  'wp.employees.ess_invite_emailed_confirmation':
    'We emailed an invitation to {email}. It contains a set-password link and a one-time code for the WagePro app, each valid once for 72 hours.',
  'wp.employees.ess_invite_link_fallback_desc':
    'Copy this link and send it to the employee — it will not be shown again. It works once and expires in 72 hours.',
  'wp.employees.ess_invite_emailed_title': 'Invite sent',
  'wp.employees.ess_invite_email_failed':
    "We couldn't email this invite. Copy the link below and send it to the employee yourself — it will not be shown again.",
  'wp.employees.ess_copy_link': 'Copy link',
  'wp.employees.ess_copy_link_failed':
    'Couldn’t copy automatically. Press and hold the link above to select it, then copy.',
  'wp.employees.ess_link_copied': 'Copied',
  'wp.employees.ess_revoke_invite_confirm_title': 'Revoke this invite?',
  'wp.employees.ess_revoke_invite_confirm_desc':
    'The pending invite will no longer be accepted. This does not affect an employee who has already linked.',
  'wp.employees.ess_revoke_access_confirm_title': 'Revoke self-service access?',
  'wp.employees.ess_revoke_access_confirm_desc':
    'The employee will lose the ability to view their own payslips. You can send a new invite later.',
  'wp.employees.ess_revoke_confirm_button': 'Revoke',
  'wp.employees.ess_loading': 'Loading self-service status…',
  'wp.employees.ess_load_error_title': "Couldn't load self-service status",
  'wp.employees.ess_load_error_description':
    "We couldn't load the self-service invites for this entity. Please try again.",
  'wp.employees.ess_load_entitlement_required_title': 'Payroll add-on required',
  'wp.employees.ess_load_entitlement_required_description':
    'Enable the Payroll add-on to invite employees to self-service.',
  'wp.employees.ess_action_error_entitlement_required':
    'Enable the Payroll add-on to manage self-service invites.',
  'wp.employees.ess_action_error_no_email':
    'Add an email address to this employee before sending an invite.',
  'wp.employees.ess_action_error_already_linked':
    'This employee already has a linked self-service account.',
  'wp.employees.ess_action_error_not_found':
    'That invite is no longer available. Refresh the page and try again.',
  'wp.employees.ess_action_error_forbidden':
    "You don't have permission to manage self-service invites.",
  'wp.employees.ess_action_error_generic':
    'Something went wrong. Please try again.',

  // ── Payroll CSV import wizard (#23315) ────────────────────────────────
  'wp.payroll_import.title': 'Import payroll from CSV',
  'wp.payroll_import.subtitle':
    'Upload a CSV of historical pay runs to seed this entity — one row per employee per period.',
  'wp.payroll_import.no_entity_title':
    'Select a company to import payroll for.',
  'wp.payroll_import.step1_title': '1. Download the template',
  'wp.payroll_import.step1_description':
    'The template lists every required column in the right order.',
  'wp.payroll_import.download_template': 'Download template',
  'wp.payroll_import.step2_title': '2. Upload your filled-in CSV',
  'wp.payroll_import.step2_description':
    'We validate every row against your employee directory before anything is written.',
  'wp.payroll_import.file_input_label': 'Payroll CSV file',
  'wp.payroll_import.malformed_header':
    'That CSV is missing required columns — download the template above and try again.',
  'wp.payroll_import.missing_columns': 'Missing columns',
  'wp.payroll_import.preview_button': 'Preview',
  'wp.payroll_import.previewing': 'Previewing…',
  'wp.payroll_import.reset': 'Start over',
  'wp.payroll_import.step3_title': '3. Review and commit',
  'wp.payroll_import.rows_previewed': 'Rows previewed',
  'wp.payroll_import.preview_errors_title':
    '{count, plural, one {# row has errors} other {# rows have errors}}',
  'wp.payroll_import.row': 'Row',
  'wp.payroll_import.more_errors':
    '…and {count, plural, one {# more error} other {# more errors}}.',
  'wp.payroll_import.commit_button': 'Import',
  'wp.payroll_import.committing': 'Importing…',
  'wp.payroll_import.commit_success_runs':
    'Imported {count, plural, one {# pay run} other {# pay runs}} with {lines} lines.',
  'wp.payroll_import.optional_columns_hint':
    'Optional columns: employee_ref, overtime, bonus, lump_sum, apit_lump_sum, other_deductions.',
  'wp.payroll_import.entitlement_required':
    'Enable the Payroll add-on to import payroll.',
  'wp.payroll_import.forbidden':
    "You don't have permission to import payroll for this company.",
  'wp.payroll_import.bad_request':
    'The CSV could not be parsed. Please check the format and try again.',
  'wp.payroll_import.generic_error': 'Something went wrong. Please try again.',
  'wp.payroll_import.file_too_large':
    'That file is too large to upload here. Split it into smaller uploads.',
  'wp.payroll_import.file_empty': 'That file is empty.',
  'wp.payroll_import.file_read':
    "We couldn't read that file. Please try again.",

  // #23311 — Leave balances panel + payout drawer (extends #23310).
  'wp.employees.leave_balances.title': 'Leave balances',
  'wp.employees.leave_balances.loading': 'Loading leave balances…',
  'wp.employees.leave_balances.column_type': 'Leave type',
  'wp.employees.leave_balances.column_days': 'Days remaining',
  'wp.employees.leave_balances.type_annual': 'Annual',
  'wp.employees.leave_balances.type_sick': 'Sick',
  'wp.employees.leave_balances.type_casual': 'Casual',
  'wp.employees.leave_balances.payout_button': 'Pay out leave',
  'wp.employees.leave_balances.entitlement_required_title':
    'Payroll add-on required',
  'wp.employees.leave_balances.entitlement_required_description':
    'Leave balances need the Payroll add-on for this business.',
  'wp.employees.leave_balances.error_title': "Couldn't load leave balances",
  'wp.employees.leave_balances.error_description':
    'Please try again in a moment.',
  'wp.employees.leave_balances.retry': 'Try again',
  'wp.employees.leave_payout.drawer_title': 'Pay out leave — {name}',
  'wp.employees.leave_payout.estimate_loading':
    'Calculating the payout estimate…',
  'wp.employees.leave_payout.estimate_error':
    "Couldn't calculate the payout estimate",
  'wp.employees.leave_payout.col_leave_type': 'Leave type',
  'wp.employees.leave_payout.col_balance_days': 'Days remaining',
  'wp.employees.leave_payout.col_payout': 'Payout',
  'wp.employees.leave_payout.total_label': 'Total payout',
  'wp.employees.leave_payout.daily_rate_note':
    'Daily rate {amount} as of {as_of}, derived from the employee’s monthly salary.',
  'wp.employees.leave_payout.date_label': 'Payout date',
  'wp.employees.leave_payout.bank_account_label': 'Bank account',
  'wp.employees.leave_payout.bank_account_placeholder': 'Choose a bank account',
  'wp.employees.leave_payout.confirm_heading': 'Confirm this payout',
  'wp.employees.leave_payout.confirm_note':
    'This will post a {amount} cash disbursement for {name} and zero out the paid-out leave balances. This action cannot be undone.',
  'wp.employees.leave_payout.continue_cta': 'Review',
  'wp.employees.leave_payout.back_cta': 'Back',
  'wp.employees.leave_payout.post_cta': 'Post payout',
  'wp.employees.leave_payout.posting': 'Posting…',
  'wp.employees.leave_payout.success': 'Leave payout posted for {name}.',
  'wp.employees.leave_payout.empty_title': 'No unused leave to pay out',
  'wp.employees.leave_payout.empty_description':
    'This employee has no balance-tracked leave remaining. Nothing to pay out.',
  'wp.employees.leave_payout.action_error_title': "Couldn't post the payout",
  'wp.employees.leave_payout.error_entitlement_required':
    'This action needs the Payroll add-on for this business.',
  'wp.employees.leave_payout.error_forbidden':
    "You don't have permission to post a leave payout.",
  'wp.employees.leave_payout.error_not_found':
    'This employee could not be found. Please refresh and try again.',
  'wp.employees.leave_payout.error_bank_account_not_found':
    'That bank account could not be found. Please pick another and try again.',
  'wp.employees.leave_payout.error_not_salaried':
    'A leave payout needs a salaried employee with a monthly salary on file.',
  'wp.employees.leave_payout.error_no_unused_balance':
    'This employee has no unused leave balance to pay out.',
  'wp.employees.leave_payout.error_stale_balance':
    'The leave balance changed while you were reviewing. Please refresh and try again.',
  'wp.employees.leave_payout.error_period_locked':
    'The payout date falls inside a locked accounting period. Choose a later date.',
  'wp.employees.leave_payout.error_generic':
    'Something went wrong. Please try again.',

  // ── WagePro auth brand panel (`wagepro_auth_layout.tsx`, shared by `/signup` and `/login`) ──
  'wp.auth_panel.headline':
    'Payroll that runs itself, for teams across Sri Lanka and the region.',
  'wp.auth_panel.bullet_pay_runs': 'Monthly pay runs approved in minutes',
  'wp.auth_panel.bullet_filings': 'EPF and ETF filings prepared for you',
  'wp.auth_panel.bullet_payslips': 'Payslips your team can actually read',
  'wp.auth_panel.language_label': 'Language',

  // ── WagePro admin signup (`wagepro_signup_screen.tsx`, WagePro signup epic) ──
  'wp.signup.title': 'Create your WagePro account',
  'wp.signup.dek': "Set up your company's payroll in one step.",
  'wp.signup.company_name_label': 'Legal Entity Name',
  'wp.signup.company_name_hint': 'Enter your company’s registered legal name.',
  'wp.signup.company_name_error': 'Enter your company’s legal entity name.',
  'wp.signup.full_name_label': 'Your full name',
  'wp.signup.full_name_error': 'Enter your full name.',
  'wp.signup.email_label': 'Email address',
  'wp.signup.email_error': 'Enter a valid email address.',
  'wp.signup.password_label': 'Password',
  'wp.signup.password_help': 'At least 12 characters.',
  'wp.signup.password_error': 'Password must be at least 12 characters.',
  'wp.signup.show_password': 'Show password',
  'wp.signup.hide_password': 'Hide password',
  'wp.auth.accept_terms': 'I agree to the Terms of Service & Privacy Policy',
  'wp.auth.accept_terms_error':
    'You must accept the Terms of Service and Privacy Policy.',
  'wp.signup.submit': 'Create account',
  'wp.signup.submitting': 'Creating your account…',
  'wp.signup.back_to_sign_in': 'Already have an account? Sign in',
  'wp.signup.account_exists_error':
    'An account already exists with this email address. Please sign in instead.',

  // ── WagePro login (`wagepro_login_screen.tsx`, WagePro signup epic) ──
  'wp.login.title': 'Sign in to WagePro',
  'wp.login.dek': 'Enter your email and password to continue.',
  'wp.login.signed_out_dek':
    "You've been signed out. Sign in again to continue.",
  'wp.login.email_label': 'Email address',
  'wp.login.email_error': 'Enter your email address.',
  'wp.login.password_label': 'Password',
  'wp.login.password_error': 'Enter your password.',
  'wp.login.show_password': 'Show password',
  'wp.login.hide_password': 'Hide password',
  'wp.login.submit': 'Sign in',
  'wp.login.submitting': 'Signing in…',
  'wp.login.create_account': 'New to WagePro? Create a company account',
  'wp.login.invite_code_link':
    'Employee signing in for the first time? Use your invite code',

  // ── ESS invite-accept (`invite_accept_screen.tsx`, WagePro signup epic) ──
  'wp.invite_accept.loading': 'Loading your invite…',
  'wp.invite_accept.invalid_link': 'This invite link is missing information.',
  'wp.invite_accept.expired':
    'This invite has expired. Ask your employer to send a new one.',
  'wp.invite_accept.title': 'Join {company}',
  'wp.invite_accept.dek_sign_in':
    'Sign in with your existing account to accept this invite.',
  'wp.invite_accept.dek_create_account':
    'Create your account to accept this invite and view your payslips.',
  'wp.invite_accept.email_label': 'Email address',
  'wp.invite_accept.full_name_label': 'Your full name',
  'wp.invite_accept.full_name_error': 'Enter your full name.',
  'wp.invite_accept.password_label': 'Password',
  'wp.invite_accept.password_error': 'Password must be at least 12 characters.',
  'wp.invite_accept.show_password': 'Show password',
  'wp.invite_accept.hide_password': 'Hide password',
  'wp.invite_accept.submit_sign_in': 'Sign in and accept',
  'wp.invite_accept.submit_create_account': 'Create account and accept',
  'wp.invite_accept.submitting': 'Submitting…',
  'wp.invite_accept.new_password_label': 'Choose a password',
  'wp.invite_accept.new_password_hint':
    'At least 12 characters. We never send passwords by email.',
  'wp.auth.confirm_password_label': 'Confirm password',
  'wp.auth.confirm_password_error': "Passwords don't match",

  // ── ESS invite one-time code (`invite_code_screen.tsx`) ──
  'wp.invite_code.title': 'Employee signing in for the first time?',
  'wp.invite_code.dek':
    "Only for employees invited by their employer. Enter your email address and the one-time code from your invitation email, then choose your own password. Employers don't need a code — sign in with your email and password.",
  'wp.invite_code.email_label': 'Email address',
  'wp.invite_code.email_error':
    'Enter the email address your invitation was sent to.',
  'wp.invite_code.code_label': 'One-time code',
  'wp.invite_code.code_hint':
    'The 10-character code from your invitation email, for example ABCDE-12345.',
  'wp.invite_code.code_error':
    'Enter the full 10-character code from your email.',
  'wp.invite_code.continue': 'Continue',
  'wp.invite_code.checking': 'Checking…',
  'wp.invite_code.back_to_sign_in': 'Already set your password? Sign in',
  // ── Leave status (cancelled) ─────────────────────────────────────────────
  'wp.leave_status.cancelled': 'Cancelled',

  // ── Leave screen: record-leave drawer ────────────────────────────────────
  'wp.leave.record_action': 'Record leave',
  'wp.leave.record_drawer_title': 'Record leave',
  'wp.leave.record_field_employee': 'Employee',
  'wp.leave.record_field_employee_placeholder': 'Select an employee',
  'wp.leave.record_field_employee_required': 'Choose an employee.',
  'wp.leave.record_field_leave_type': 'Leave type',
  'wp.leave.record_field_leave_type_placeholder': 'Select leave type',
  'wp.leave.record_field_leave_type_required': 'Choose a leave type.',
  'wp.leave.record_field_leave_type_load_error':
    'Could not load leave types. Please try again.',
  'wp.leave.record_field_start_date': 'Start date',
  'wp.leave.record_field_end_date': 'End date',
  'wp.leave.record_field_dates_required':
    'Both a start and end date are required.',
  'wp.leave.record_field_days': 'Days',
  'wp.leave.record_field_days_hint':
    'Business days are calculated automatically. You can override this.',
  'wp.leave.record_field_days_invalid': 'Enter a positive number of days.',
  'wp.leave.record_field_notes': 'Notes',
  'wp.leave.record_field_notes_placeholder': 'Optional notes about this leave…',
  'wp.leave.record_submit': 'Save leave',
  'wp.leave.record_submitting': 'Saving…',
  'wp.leave.record_error_title': 'Could not record leave',
  'wp.leave.record_error_overlap_title': 'Overlapping leave request',
  'wp.leave.record_error_overlap':
    'This employee already has a leave request overlapping these dates. Change the date range and try again.',
  'wp.leave.record_error_invalid_dates':
    'The end date must be on or after the start date.',
  'wp.leave.record_error_unknown_leave_type':
    'That leave type is not active. Choose another type.',
  'wp.leave.record_error_entitlement_required':
    'Recording leave requires the Payroll add-on.',
  'wp.leave.record_error_forbidden':
    "You don't have permission to record leave.",
  'wp.leave.record_error_not_found':
    'The employee could not be found. Refresh and try again.',
  'wp.leave.record_error_generic': 'Something went wrong. Please try again.',

  // ── Leave screen: cancel action ───────────────────────────────────────────
  'wp.leave.cancel_action': 'Cancel',
  'wp.leave.cancel_confirm_title': 'Cancel leave request?',
  'wp.leave.cancel_confirm_description':
    "Cancelling this request will restore the employee's leave balance where applicable.",
  'wp.leave.cancel_confirm_button': 'Cancel leave',
  'wp.leave.cancel_error_entitlement_required':
    'Cancelling leave requires the Payroll add-on.',
  'wp.leave.cancel_error_forbidden':
    "You don't have permission to cancel leave requests.",
  'wp.leave.cancel_error_not_found':
    'This leave request no longer exists. Refresh the list and try again.',
  'wp.leave.cancel_error_already_terminal':
    'This request has already been cancelled or rejected — it cannot be cancelled again.',
  'wp.leave.cancel_error_pay_run_overlap':
    'This approved leave overlaps a completed pay run and cannot be cancelled here. Contact support.',
  'wp.leave.cancel_error_generic': 'Something went wrong. Please try again.',

  // ── Leave screen: balances tab ─────────────────────────────────────────────
  'wp.leave.tab_balances': 'Balances',
  'wp.leave.balances_loading': 'Loading leave balances…',
  'wp.leave.balances_empty_title': 'No leave balances recorded yet',
  'wp.leave.balances_empty_description':
    'Once employees have leave balances on file, they will appear here.',
  'wp.leave.balances_error_title': "Couldn't load leave balances",
  'wp.leave.balances_error_description':
    'Something went wrong loading the leave balance list.',
  'wp.leave.balances_column_employee': 'Employee',
  'wp.leave.balances_column_type': 'Type',
  'wp.leave.balances_column_total': 'Total',
  'wp.leave.balances_column_taken': 'Taken',
  'wp.leave.balances_column_booked': 'Booked',
  'wp.leave.balances_column_pending': 'Pending',
  'wp.leave.balances_column_available': 'Available',
  'wp.leave.recalculate_action': 'Recalculate entitlements',
  'wp.leave.recalculate_done':
    '{count, plural, =0 {All balances are already up to date.} one {Updated # balance.} other {Updated # balances.}}',
  'wp.leave.recalculate_forbidden':
    'Only an admin or owner can recalculate entitlements.',
  'wp.leave.recalculate_error':
    "Couldn't recalculate entitlements. Please try again.",
  'wp.leave.balances_adjust_action': 'Adjust',

  // ── Leave: record opening balance (#24315) ────────────────────────────────
  'wp.leave.record_opening_balance_action': 'Record opening balance',
  'wp.leave.opening_balance_drawer_title': 'Record opening leave balance',
  'wp.leave.opening_balance_drawer_description':
    'Pick an employee and a balance-tracked leave type to seed or correct their opening balance.',
  'wp.leave.opening_balance_employee_label': 'Employee',
  'wp.leave.opening_balance_employee_placeholder': 'Choose an employee',
  'wp.leave.opening_balance_employee_required': 'Choose an employee.',
  'wp.leave.opening_balance_type_label': 'Leave type',
  'wp.leave.opening_balance_type_placeholder': 'Choose a leave type',
  'wp.leave.opening_balance_type_required': 'Choose a leave type.',
  'wp.leave.opening_balance_continue': 'Continue',
  'wp.leave.opening_balance_no_employees_title': 'No employees yet',
  'wp.leave.opening_balance_no_employees_description':
    'Add an employee before recording an opening leave balance.',
  'wp.leave.opening_balance_no_leave_types_title':
    'No balance-tracked leave types',
  'wp.leave.opening_balance_no_leave_types_description':
    'Set up an annual, sick, or casual leave type in Settings → Leave types before recording an opening balance.',
  'wp.leave.opening_balance_employees_error_title': "Couldn't load employees",
  'wp.leave.opening_balance_employees_error_description':
    'Something went wrong loading the employee list. Refresh and try again.',

  // ── Leave: adjust-balance drawer ──────────────────────────────────────────
  'wp.leave.adjust_drawer_title': 'Adjust leave balance',
  'wp.leave.adjust_drawer_description':
    'Adjusting the balance for {leave_type} (employee {employee}).',
  'wp.leave.adjust_direction_label': 'Direction',
  'wp.leave.adjust_days_label': 'Days',
  'wp.leave.adjust_days_invalid': 'Enter a positive number of days.',
  'wp.leave.adjust_reason_label': 'Reason',
  'wp.leave.adjust_reason_placeholder': 'Reason for the adjustment…',
  'wp.leave.adjust_reason_required':
    'A reason of at least 3 characters is required.',
  'wp.leave.adjust_submit': 'Apply adjustment',
  'wp.leave.adjust_submitting': 'Applying…',
  'wp.leave.adjust_error_title': 'Could not adjust balance',
  'wp.leave.adjust_error_generic': 'Something went wrong. Please try again.',

  // ── Leave employees panel ─────────────────────────────────────────────────
  'wp.employees.leave_balances.adjust_button': 'Adjust',

  // ── Settings: leave types ─────────────────────────────────────────────────
  'wp.settings.tab_leave_types': 'Leave types',
  'wp.settings.tab_import': 'Data import',
  'wp.settings.tab_security': 'Security',
  'wp.settings.leave_types.title': 'Leave types',
  'wp.settings.leave_types.description':
    'Configure the leave categories available to employees at this entity.',
  'wp.settings.leave_types.allow_negative_balance':
    'Allow negative leave balances',
  'wp.settings.leave_types.allow_negative_balance_on_hint':
    'Leave can be approved even when the employee’s balance is insufficient. The balance will go negative.',
  'wp.settings.leave_types.allow_negative_balance_off_hint':
    'Leave approval is blocked when the employee’s balance is zero or insufficient.',
  'wp.settings.leave_types.loading': 'Loading leave types…',
  'wp.settings.leave_types.empty': 'No leave types configured yet.',
  'wp.settings.leave_types.error_title': "Couldn't load leave types",
  'wp.settings.leave_types.error_description':
    'Something went wrong loading the leave type list.',
  'wp.settings.leave_types.add_button': 'Add leave type',
  'wp.settings.leave_types.save': 'Save',
  'wp.settings.leave_types.save_error_title': 'Could not save leave type',
  'wp.settings.leave_types.system_badge': 'System',
  'wp.settings.leave_types.paid': 'Paid',
  'wp.settings.leave_types.unpaid': 'Unpaid',
  'wp.settings.leave_types.tracks_balance': 'Tracks balance',
  'wp.settings.leave_types.no_balance': 'No balance tracking',
  'wp.settings.leave_types.field_code': 'Code',
  'wp.settings.leave_types.field_name': 'Name',
  'wp.settings.leave_types.field_accrual': 'Accrual method',
  'wp.settings.leave_types.field_entitlement': 'Annual entitlement (days)',
  'wp.settings.leave_types.field_is_paid': 'Paid leave',
  'wp.settings.leave_types.field_tracks_balance': 'Track balance',
  'wp.settings.leave_types.accrual_fixed_annual': 'Fixed annual allowance',
  'wp.settings.leave_types.accrual_none': 'No accrual (unlimited)',
  'wp.settings.leave_types.accrual_statutory': 'Statutory',
  'wp.settings.leave_types.accrual_monthly': 'Monthly accrual',
  'wp.settings.leave_types.monthly_hint':
    'Accrues {days} days on the 1st of each month.',
  'wp.settings.leave_types.field_leave_year_start': 'Leave year starts',
  'wp.settings.leave_types.field_carry_forward': 'Carry-forward',
  'wp.settings.leave_types.field_carry_forward_max': 'Max days carried forward',
  'wp.settings.leave_types.carry_forward_unlimited': 'Unlimited',
  'wp.settings.leave_types.carry_forward_capped': 'Limited',
  'wp.settings.leave_types.field_prorate_on_join':
    'Pro-rate for mid-year joiners',
  'wp.settings.leave_types.policy_applies_hint':
    'Saving applies this policy to all current and new employees. Manually adjusted balances are kept.',
  'wp.settings.leave_types.summary_fixed': '{days} days / year',
  'wp.settings.leave_types.summary_monthly':
    '{days} days / year, accrued monthly',
  'wp.settings.leave_types.summary_not_configured': 'Entitlement not set',
  'wp.settings.leave_types.error_carry_forward_invalid':
    'Enter the maximum days to carry forward (0 or more).',
  'wp.settings.leave_groups.title': 'Leave policy groups',
  'wp.settings.leave_groups.description':
    'Give some employees a different leave policy, for example 21 annual days for managers while everyone else keeps the default.',
  'wp.settings.leave_groups.add_button': 'Add group',
  'wp.settings.leave_groups.field_name': 'Group name',
  'wp.settings.leave_groups.field_members': 'Members',
  'wp.settings.leave_groups.override_label': 'Override {leave_type}',
  'wp.settings.leave_groups.accrual_none': 'No entitlement',
  'wp.settings.leave_groups.no_types':
    'There are no fixed-annual or monthly leave types to override yet.',
  'wp.settings.leave_groups.no_employees': 'No employees yet.',
  'wp.settings.leave_groups.member_of_other': '(currently in {group})',
  'wp.settings.leave_groups.save_error_title': 'Could not save group',
  'wp.settings.leave_groups.error_forbidden':
    'Only an admin or owner can manage leave policy groups.',
  'wp.settings.leave_groups.error_invalid':
    'The group could not be saved. Check the leave type overrides.',
  'wp.settings.leave_groups.error_save_generic':
    'Something went wrong saving the group. Please try again.',
  'wp.settings.leave_groups.error_members':
    'The group was saved, but some members could not be updated. Please try again.',
  'wp.settings.leave_groups.error_title': "Couldn't load leave policy groups",
  'wp.settings.leave_groups.error_description':
    'Refresh the page to try again.',
  'wp.settings.leave_groups.empty':
    "No policy groups yet. Every employee uses the leave types' default policies.",
  'wp.settings.leave_groups.summary':
    '{members, plural, one {# member} other {# members}} · {rules, plural, one {# override} other {# overrides}}',
  'wp.settings.leave_groups.error_code_format':
    'Use 1-32 lowercase letters, digits or underscores.',
  'wp.settings.leave_groups.error_name_required': 'A group name is required.',
  'wp.settings.leave_groups.error_rule_invalid':
    'Enter valid days for each override (0 to 366).',
  'wp.settings.leave_types.error_code_required': 'A code is required.',
  'wp.settings.leave_types.error_code_format':
    'Code must be lowercase letters, numbers, or underscores (max 32 chars).',
  'wp.settings.leave_types.error_name_required': 'A name is required.',
  'wp.settings.leave_types.error_entitlement_required':
    'Annual entitlement is required for a fixed annual allowance type.',
  'wp.settings.leave_types.error_entitlement_required_upsert':
    'This leave type requires the Payroll add-on.',
  'wp.settings.leave_types.error_forbidden':
    "You don't have permission to edit leave types.",
  'wp.settings.leave_types.error_not_found':
    'This leave type no longer exists. Refresh and try again.',
  'wp.settings.leave_types.error_save_generic':
    'Something went wrong saving this leave type. Please try again.',
  // ── My Leave ESS (Phase 5b) ───────────────────────────────────────────────
  'wp.my_leave.title': 'My leave',
  'wp.my_leave.subtitle': '{employer}',
  'wp.my_leave.loading': 'Loading your leave…',
  'wp.my_leave.error':
    'Something went wrong loading your leave. Please try again.',
  'wp.my_leave.retry': 'Try again',
  'wp.my_leave.no_employer_link':
    "Your employer hasn't set up your leave access yet. Accept the invitation from your employer first.",
  'wp.my_leave.step_up.title': 'Two-factor authentication required',
  'wp.my_leave.step_up.description':
    'Viewing your leave balance needs a stronger sign-in than this session has. Try again, and ask your administrator if it keeps happening.',
  'wp.my_leave.step_up.retry_cta': 'Try again',
  'wp.my_leave.ess_nav_label': 'Employee self-service navigation',
  'wp.my_leave.balances_section_label': 'Leave balances',
  'wp.my_leave.days_available': '{count} days available',
  'wp.my_leave.leave_type.annual': 'Annual leave',
  'wp.my_leave.leave_type.sick': 'Sick leave',
  'wp.my_leave.leave_type.casual': 'Casual leave',
  'wp.my_leave.leave_type.unpaid': 'Unpaid leave',
  'wp.my_leave.leave_type.other': 'Other',
  'wp.my_leave.request_section_title': 'Leave requests',
  'wp.my_leave.request_cta': 'Request leave',
  'wp.my_leave.cancel_form_cta': 'Cancel',
  'wp.my_leave.notice.approved_title': 'Leave approved',
  'wp.my_leave.notice.rejected_title': 'Leave request declined',
  'wp.my_leave.notice.body':
    '{employer} reviewed your {leave_type} request for {start_date} to {end_date}.',
  'wp.my_leave.history_title': 'Request history',
  'wp.my_leave.history_empty': 'No leave requests yet.',
  'wp.my_leave.col_type': 'Type',
  'wp.my_leave.col_dates': 'Dates',
  'wp.my_leave.col_days': 'Days',
  'wp.my_leave.days_count': '{count, plural, one {# day} other {# days}}',
  'wp.my_leave.col_status': 'Status',
  'wp.my_leave.cancel_request_cta': 'Cancel',
  'wp.my_leave.cancel_dialog_title': 'Cancel leave request',
  'wp.my_leave.cancel_dialog_description':
    'Are you sure you want to cancel this leave request? Your employer will be notified.',
  'wp.my_leave.cancel_dialog_confirm_label': 'Yes, cancel request',
  'wp.my_leave.cancel_error_generic': 'Something went wrong. Please try again.',
  'wp.my_leave.form.leave_type_label': 'Leave type',
  'wp.my_leave.form.leave_type_placeholder': 'Select a leave type',
  'wp.my_leave.form.start_date_label': 'From',
  'wp.my_leave.form.end_date_label': 'To',
  'wp.my_leave.form.notes_label': 'Notes (optional)',
  'wp.my_leave.form.notes_placeholder':
    'Any additional details for your manager…',
  'wp.my_leave.form.submit_cta': 'Submit request',
  'wp.my_leave.form.submitting': 'Submitting…',
  'wp.my_leave.form.error.leave_type_required': 'Please select a leave type.',
  'wp.my_leave.form.error.start_date_required': 'Please select a start date.',
  'wp.my_leave.form.error.end_date_required': 'Please select an end date.',
  'wp.my_leave.form.error.end_before_start':
    'The end date must be on or after the start date.',
  'wp.my_leave.form.error.overlap':
    'This request overlaps an existing pending or approved leave request.',
  'wp.my_leave.form.error.invalid_type':
    'This leave type is not available. Please select another.',
  'wp.my_leave.form.error.unknown': 'Something went wrong. Please try again.',
  'wp.my_profile.title': 'Profile',
  'wp.my_profile.language': 'Language',
  'wp.employees.compensation_effective_from': 'Salary effective from',
  'wp.employees.compensation_effective_from_hint':
    'Defaults to the employment start date',
  'wp.employees.compensation_history_title': 'Earlier salary entries',
  'wp.employees.compensation_history_add': 'Add earlier entry',
  'wp.employees.compensation_history_remove': 'Remove',
  'wp.employees.compensation_col_effective_from': 'Effective from',
  'wp.employees.compensation_col_amount': 'Amount',
  'wp.employees.change_salary': 'Change salary',
  'wp.employees.change_salary_title': 'Change salary',
  'wp.employees.change_salary_description':
    'Schedule a new salary or hourly rate from a chosen date. Approved pay runs are never changed.',
  'wp.employees.change_salary_submit': 'Save change',
  'wp.employees.compensation_current': 'Current pay',
  'wp.employees.compensation_edit_hint':
    'Pay is changed with an effective date, so approved pay runs are never altered.',
  'wp.employees.compensation_in_effect_since': 'In effect since {date}',
  'wp.employees.compensation_reason': 'Reason',
  'wp.employees.compensation_reason.hire': 'Hire',
  'wp.employees.compensation_reason.promotion': 'Promotion',
  'wp.employees.compensation_reason.annual_review': 'Annual review',
  'wp.employees.compensation_reason.correction': 'Correction',
  'wp.employees.compensation_reason.other': 'Other',
  'wp.employees.compensation_note': 'Note',
  'wp.employees.compensation_status.scheduled': 'Scheduled',
  'wp.employees.compensation_status.current': 'Current',
  'wp.employees.compensation_status.past': 'Past',
  'wp.employees.compensation_status.cancelled': 'Cancelled',
  'wp.employees.compensation_status.superseded': 'Superseded',
  'wp.employees.compensation_cancel': 'Cancel change',
  'wp.employees.compensation_cancel_keep': 'Keep change',
  'wp.employees.compensation_cancel_confirm':
    'Cancel the salary change scheduled for {date}?',
  'wp.employees.compensation_cancel_consumed':
    'This change was already used by an approved pay run and cannot be cancelled. Record a correcting change instead.',
  'wp.employees.compensation_cancel_error':
    'The change could not be cancelled. Please try again.',
  'wp.employees.compensation_applied_today': 'Salary updated',
  'wp.employees.compensation_scheduled_for':
    'Salary change scheduled for {date}',
  'wp.employees.compensation_back_pay_notice':
    'Approved pay runs for {periods} stay unchanged. The difference will be added as back pay on the next pay run.',
  'wp.employees.compensation_currency_boundary_hint':
    'A currency change must start on a pay-period boundary.',
  'wp.employees.compensation_draft_conflict_title':
    'A draft pay run is in the way',
  'wp.employees.compensation_conflict_title': 'This change cannot be scheduled',
  'wp.employees.compensation_replace_same_date': 'Replace existing entry',
  'wp.employees.compensation_no_history': 'No salary history yet',
  'wp.employees.compensation_no_history_description':
    'Only the current pay is on record so far.',
  'wp.employees.compensation_load_error': 'Could not load salary history',
  'wp.employees.compensation_retry': 'Try again',
  'wp.employees.compensation_error_hourly_rate_required':
    'Enter an hourly rate greater than zero.',
  'wp.employees.change_salary_error_after_termination':
    'Must be on or before the termination date.',
  'wp.employees.form_error_effective_from_before_start':
    'Must be on or after the employment start date',
  'wp.employees.form_error_history_date_duplicate':
    'Two entries share the same date',
  'wp.employees.form_error_history_date_not_before':
    'Earlier entries must be dated before the current salary',
  'wp.employees.form_error_history_amount_required': 'Enter an amount',
  'wp.employees.form_error_history_invalid':
    'Fix the highlighted salary entries.',
  'wp.pay_run.back_pay': 'Back pay',
  'wp.pay_run.salary_changed_in_period': 'Salary changed during this period',
  'wp.pay_run.back_pay_warning_title': 'Back pay needs a manual adjustment',
  'wp.pay_run.back_pay_manual_warning':
    'Back pay for pay runs created before salary history existed must be added with a one-off adjustment.',
  'wp.pay_run.back_pay_currency_warning':
    'Back pay could not be calculated for a pay run because the pay currency changed since. Add a one-off adjustment if it applies.',

  // ── Two-step verification (`wagepro_two_factor.ts`, sign-in steps + Settings › Security) ──
  'wp.two_factor.section_title': 'Two-step verification',
  'wp.two_factor.section_dek':
    'Protect your account with a code from an authenticator app each time you sign in. WagePro holds salaries, ID numbers and bank details, so we strongly recommend it.',
  'wp.two_factor.status_on': 'On',
  'wp.two_factor.status_off': 'Off',
  'wp.two_factor.managed_in_ledger':
    'You signed in with your LedgerPro account, so two-step verification is managed in LedgerPro, under Settings › Security.',
  'wp.two_factor.turn_on': 'Turn on two-step verification',
  'wp.two_factor.turn_off': 'Turn off two-step verification',
  'wp.two_factor.enable_intro':
    'You will need an authenticator app on your phone, such as Google Authenticator, Microsoft Authenticator or 1Password.',
  'wp.two_factor.get_started': 'Get started',
  'wp.two_factor.scan_step':
    'Scan this QR code with your authenticator app, then enter the 6-digit code it shows.',
  'wp.two_factor.manual_key_label':
    'Cannot scan it? Enter this key in the app instead:',
  'wp.two_factor.qr_alt': 'QR code for setting up your authenticator app',
  'wp.two_factor.code_label': '6-digit code',
  'wp.two_factor.current_password_label': 'Current password',
  'wp.two_factor.recovery_title': 'Save your recovery codes',
  'wp.two_factor.recovery_dek':
    'If you lose your phone, each of these codes lets you sign in once. This is the only time they will be shown, so store them somewhere safe.',
  'wp.two_factor.recovery_copy': 'Copy codes',
  'wp.two_factor.recovery_copied': 'Copied',
  'wp.two_factor.recovery_confirm': 'I have saved my recovery codes',
  'wp.two_factor.recovery_remaining':
    '{count, plural, one {# recovery code left} other {# recovery codes left}}',
  'wp.two_factor.done': 'Done',
  'wp.two_factor.disable_intro':
    'Enter a code from your authenticator app (or a recovery code) and your password to turn two-step verification off.',
  'wp.two_factor.code_or_recovery_label': 'Authenticator code or recovery code',
  'wp.two_factor.required_cannot_disable':
    'Your company requires two-step verification, so it cannot be turned off.',
  'wp.two_factor.required_banner_title':
    'Your company requires two-step verification',
  'wp.two_factor.required_banner_body':
    'Turn it on now. You will be asked to set it up the next time you sign in anyway.',
  'wp.two_factor.company_title':
    'Require two-step verification for your company',
  'wp.two_factor.company_dek':
    'Everyone on your company team who signs in to WagePro will have to use two-step verification. Employees who only view their own payslips are not affected.',
  'wp.two_factor.company_enable_self_first':
    'Turn on two-step verification for your own account first.',
  'wp.two_factor.code_title': 'Enter your verification code',
  'wp.two_factor.code_dek':
    'Open your authenticator app and enter the 6-digit code for WagePro.',
  'wp.two_factor.recovery_code_dek':
    'Enter one of the recovery codes you saved when you set up two-step verification. Each code works once.',
  'wp.two_factor.recovery_code_label': 'Recovery code',
  'wp.two_factor.verify': 'Verify',
  'wp.two_factor.verifying': 'Verifying…',
  'wp.two_factor.use_recovery_code': 'Use a recovery code instead',
  'wp.two_factor.use_authenticator': 'Use my authenticator app instead',
  'wp.two_factor.back_to_sign_in': 'Back to sign in',
  'wp.two_factor.start_over': 'Sign in again',
  'wp.two_factor.setup_required_title': 'Set up two-step verification',
  'wp.two_factor.setup_required_dek':
    'Your company requires two-step verification. Set it up now to finish signing in. It takes about a minute.',
  'wp.two_factor.continue_to_wagepro': 'Continue to WagePro',
  'wp.two_factor.error_invalid_code':
    'That code did not work. Check your authenticator app and try again.',
  'wp.two_factor.error_expired':
    'This sign-in has expired. Please sign in again.',
  'wp.two_factor.error_too_many_attempts':
    'Too many attempts. Please wait 15 minutes and try again.',
  'wp.two_factor.error_wrong_password': 'Your password is incorrect.',
  'wp.two_factor.error_required_by_company':
    'Your company requires two-step verification, so it cannot be turned off.',
  'wp.two_factor.error_enable_self_first':
    'Turn on two-step verification for your own account first.',

  // ── WagePro migration import (wmi) ──
  'wp.migration.title': 'Import employees',
  'wp.migration.subtitle':
    'Bring employees, pay history and leave balances across from your old system.',
  'wp.migration.no_entity': 'Select a company before importing.',
  'wp.migration.read_only':
    'You have read-only access and cannot run an import.',
  'wp.migration.step.start': 'Start',
  'wp.migration.step.template': 'Template',
  'wp.migration.step.upload': 'Upload',
  'wp.migration.step.map': 'Map columns',
  'wp.migration.step.review': 'Review',
  'wp.migration.step.import': 'Import',
  'wp.migration.step.done': 'Done',
  'wp.migration.button.back': 'Back',
  'wp.migration.button.next': 'Next',
  'wp.migration.button.cancel': 'Cancel',
  'wp.migration.button.import': 'Import',
  'wp.migration.button.download_xlsx': 'Download Excel template',
  'wp.migration.button.download_csv': 'Download CSV files',
  'wp.migration.start.description':
    'Choose what to bring across and the date you switch to WagePro.',
  'wp.migration.scope.title': 'What do you want to import?',
  'wp.migration.scope.full.label': 'Everything',
  'wp.migration.scope.full.help':
    'Employees, salary history, payroll history and leave balances.',
  'wp.migration.scope.payroll_only.label': 'Payroll history only',
  'wp.migration.scope.payroll_only.help':
    'Past payslips for employees already in WagePro.',
  'wp.migration.cutover.label': 'Cut-over date',
  'wp.migration.cutover.help':
    'The day you start running payroll in WagePro. History is imported up to this date.',
  'wp.migration.template.description':
    'Download the template, fill it in, then upload it on the next step.',
  'wp.migration.template.existing_warning':
    'Some employees are already in WagePro. Rows with an existing employee number are skipped.',
  'wp.migration.upload.description':
    'Upload the Excel workbook, or one CSV per sheet.',
  'wp.migration.upload.prompt': 'Drag files here',
  'wp.migration.upload.subtext': '.xlsx or .csv, up to 10 MB each',
  'wp.migration.upload.browse': 'Browse files',
  'wp.migration.upload.detected': 'Detected sheets',
  'wp.migration.upload.need_employees':
    'An Employees sheet is required to continue.',
  'wp.migration.upload.parse_error':
    'That file could not be read. Save it as CSV UTF-8 or .xlsx and try again.',
  'wp.migration.map.description':
    'Match each column in your file to a WagePro field.',
  'wp.migration.map.target_field': 'WagePro field',
  'wp.migration.map.ignore': 'Ignore this column',
  'wp.migration.review.validating': 'Checking your data…',
  'wp.migration.review.no_report':
    'Nothing has been checked yet. Go back and upload your file.',
  'wp.migration.review.blocked_title': 'Fix these before importing',
  'wp.migration.review.blocked_body':
    '{count} rows have errors that must be fixed.',
  'wp.migration.review.warnings_title': 'Please review these warnings',
  'wp.migration.review.warnings_body':
    '{count} rows have warnings. Check them, then confirm to continue.',
  'wp.migration.review.ready_title': 'Ready to import',
  'wp.migration.review.ready_body': 'No problems found. You can import now.',
  'wp.migration.review.col_sheet': 'Sheet',
  'wp.migration.review.col_row': 'Row',
  'wp.migration.review.col_severity': 'Type',
  'wp.migration.review.col_message': 'What to do',
  'wp.migration.review.acknowledge':
    "I've reviewed the warnings and want to continue.",
  'wp.migration.counts.employees_create': 'New employees',
  'wp.migration.counts.employees_link': 'Linked employees',
  'wp.migration.counts.pay_periods': 'Pay periods',
  'wp.migration.counts.leave_rows': 'Leave balances',
  'wp.migration.import.progress': 'Imported {loaded} of {total}',
  'wp.migration.import.safe_to_leave':
    'You can leave this page — the import keeps running and you can come back.',
  'wp.migration.import.stalled_title': 'Import paused',
  'wp.migration.import.stalled_body':
    'The import stopped part-way. Select Continue to resume where it left off.',
  'wp.migration.import.continue': 'Continue import',
  'wp.migration.done.title': 'Import complete',
  'wp.migration.done.body':
    'Your employees, pay history and leave balances are now in WagePro.',
  'wp.migration.done.loaded': 'Records imported',
  'wp.migration.done.total': 'Total records',
  'wp.migration.done.next_pay_run': 'Start your first pay run',
  'wp.migration.done.finish': 'Finish',
  'wp.migration.done.undo': 'Undo import',
  'wp.migration.undo.confirm_title': 'Undo this import?',
  'wp.migration.undo.confirm_body':
    "This voids the imported pay runs, reverses the opening leave balances and deactivates the employees this import created. It can't be undone once a real pay run has been approved.",
  'wp.migration.error.file_too_large':
    'That file is too large. Keep each file under 10 MB.',
  'wp.migration.error.generic': 'Something went wrong. Please try again.',
  'wp.migration.sheet.Employees': 'Employees',
  'wp.migration.sheet.Salary_History': 'Salary history',
  'wp.migration.sheet.Payroll_History': 'Payroll history',
  'wp.migration.sheet.Leave_Balances': 'Leave balances',
  'wp.migration.sheet.file': 'File',
  'wp.migration.severity.error': 'Error',
  'wp.migration.severity.warning': 'Warning',
  'wp.migration.severity.info': 'Info',
  'wp.migration.issue.MIGRATION_FILE_UNREADABLE':
    'The file could not be read. Save it as .xlsx or CSV UTF-8.',
  'wp.migration.issue.MIGRATION_BAD_ENCODING':
    "The text encoding isn't valid. Re-save the file as CSV UTF-8.",
  'wp.migration.issue.MIGRATION_MISSING_SHEET':
    'A required sheet is missing from the workbook.',
  'wp.migration.issue.MIGRATION_MISSING_COLUMN':
    'A required column is missing. Use the template headers.',
  'wp.migration.issue.MIGRATION_DUPLICATE_HEADER':
    'Two columns have the same header. Make each header unique.',
  'wp.migration.issue.MIGRATION_EMPTY_TEMPLATE':
    'This sheet has no data rows. Add at least one employee.',
  'wp.migration.issue.MIGRATION_UNKNOWN_SHEET':
    "This sheet wasn't recognised. Assign it a type or remove it.",
  'wp.migration.issue.MIGRATION_REQUIRED': 'This value is required.',
  'wp.migration.issue.MIGRATION_INVALID_VALUE':
    "This value isn't valid for this column.",
  'wp.migration.issue.MIGRATION_INVALID_ENUM':
    'Use one of the allowed values from the dropdown.',
  'wp.migration.issue.MIGRATION_TOO_LONG': 'This value is too long.',
  'wp.migration.issue.MIGRATION_INVALID_DATE':
    'Enter a real date as YYYY-MM-DD.',
  'wp.migration.issue.MIGRATION_FUTURE_DATE':
    "This date can't be in the future.",
  'wp.migration.issue.MIGRATION_BAD_MONEY':
    'Enter a plain amount like 125000.00 — no symbols or spaces.',
  'wp.migration.issue.MIGRATION_NEGATIVE_MONEY':
    "This amount can't be negative.",
  'wp.migration.issue.MIGRATION_TOO_MANY_DECIMALS':
    'Use at most two decimal places.',
  'wp.migration.issue.MIGRATION_SCI_NOTATION':
    'This looks like 2.00E+11. Format the column as Text and re-enter the full number.',
  'wp.migration.issue.MIGRATION_LOST_LEADING_ZERO':
    'A leading zero may have been lost. Format the column as Text.',
  'wp.migration.issue.MIGRATION_INVALID_EMAIL': 'Enter a valid email address.',
  'wp.migration.issue.MIGRATION_INVALID_NIC': "This NIC number isn't valid.",
  'wp.migration.issue.MIGRATION_INVALID_NIC_DAY':
    'The day-of-year in this NIC is out of range.',
  'wp.migration.issue.MIGRATION_INVALID_EPF':
    "This EPF member number isn't valid.",
  'wp.migration.issue.MIGRATION_INVALID_NIK':
    "This NIK isn't valid. It must be 16 digits.",
  'wp.migration.issue.MIGRATION_INVALID_NPWP': "This NPWP isn't valid.",
  'wp.migration.issue.MIGRATION_INVALID_PTKP':
    'Choose a valid PTKP status (e.g. TK/0, K/1).',
  'wp.migration.issue.MIGRATION_INVALID_RISK_CLASS':
    'Choose a BPJS JKK risk class from 1 to 5.',
  'wp.migration.issue.MIGRATION_NET_IDENTITY':
    'Net pay must equal gross plus THR minus deductions, to the cent.',
  'wp.migration.issue.MIGRATION_GROSS_SUM':
    'Gross must equal basic plus overtime, bonus and lump sum.',
  'wp.migration.issue.MIGRATION_PERIOD_OUTSIDE_EMPLOYMENT':
    "This pay period falls outside the employee's dates of employment.",
  'wp.migration.issue.MIGRATION_CONTRACTOR_IN_PAYROLL':
    "Contractors can't appear in payroll history. Remove this row.",
  'wp.migration.issue.MIGRATION_DUPLICATE_EMPLOYEE_NUMBER':
    'This employee number is used more than once. Make it unique.',
  'wp.migration.issue.MIGRATION_DUPLICATE_EMPLOYEE_PERIOD':
    'This employee appears twice in the same pay period.',
  'wp.migration.issue.MIGRATION_UNKNOWN_EMPLOYEE':
    'No employee with this number is in the file or in WagePro.',
  'wp.migration.issue.MIGRATION_INCONSISTENT_PERIOD':
    "Pay periods overlap or don't line up. Use one clean period per month.",
  'wp.migration.issue.MIGRATION_MISSING_MONTH':
    'A month of payroll is missing for this employee in the current tax year.',
  'wp.migration.issue.MIGRATION_YTD_ROW_REJECTED':
    "A single year-to-date total isn't accepted. Provide one row per month.",
  'wp.migration.issue.MIGRATION_SALARY_MISMATCH':
    "The latest salary-history row must match the employee's current salary.",
  'wp.migration.issue.MIGRATION_EPF_RATE_WARNING':
    "EPF doesn't look like the usual 8% employee / 12% employer. Please double-check.",
  'wp.migration.issue.MIGRATION_ETF_RATE_WARNING':
    "ETF doesn't look like the usual 3%. Please double-check.",
  'wp.migration.issue.MIGRATION_TABLE5_WARNING':
    'This APIT differs from the standard monthly table, so this employee will move to the cumulative (Table 5) method for the rest of the year.',
  'wp.migration.issue.MIGRATION_APIT_PERIOD_UNSUPPORTED':
    'This pay period is before 1 April 2023, so Ledger cannot check its APIT against the IRD tables. The figures are stored as given.',
  'wp.migration.issue.MIGRATION_PPH21_TER_WARNING':
    'PPh 21 looks far from the expected TER amount. Please double-check.',
  'wp.migration.issue.MIGRATION_BPJS_DERIVE_WARNING':
    "BPJS doesn't match what we'd calculate from gross pay. Please double-check.",
  'wp.migration.issue.MIGRATION_PLAN_CAP':
    "This would exceed your plan's employee limit. Upgrade or import fewer active employees.",
  'wp.migration.issue.MIGRATION_OVERLAPPING_RUNS':
    'A pay run already covers this period. Remove the overlapping rows.',
  'wp.migration.issue.MIGRATION_CURRENCY_NOT_ENABLED':
    'Payroll history can only be imported for employees paid in your base currency.',
  'wp.migration.issue.MIGRATION_UNKNOWN_LEAVE_TYPE':
    "This leave type doesn't exist in WagePro. Create it first or fix the code.",
  'wp.migration.issue.MIGRATION_NIC_CLASH':
    'An employee with this NIC already exists. Link to them or remove the row.',
  'wp.migration.issue.MIGRATION_NIK_CLASH':
    'An employee with this NIK already exists. Link to them or remove the row.',
  'wp.migration.issue.MIGRATION_NPWP_CLASH':
    'An employee with this NPWP already exists. Link to them or remove the row.',
  'wp.migration.issue.MIGRATION_EMAIL_CLASH':
    'An employee with this email already exists. Link to them or remove the row.',
  'wp.migration.issue.MIGRATION_EMPLOYEE_EXISTS':
    'This employee number already exists in WagePro and will be skipped.',
  'wp.migration.undo.failed':
    'Undo is not available — a payroll run was approved, a period is locked, or a statutory payment references this import.',
  'wp.migration.issue.MIGRATION_DUPLICATE_LEAVE_TYPE':
    'This employee already has a row for this leave type. Keep only one.',
  'wp.migration.issue.MIGRATION_DUPLICATE_SALARY_DATE':
    'This employee already has a salary row for this effective date. Keep only one.',
  'wp.migration.import.failed_title': 'Import failed',
  'wp.migration.import.failed_body':
    "The import stopped and can't be resumed. Nothing partial was left behind — start a new import or contact support.",
};
